import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTenantProfile, useTenantApiToken } from '../contexts/TenantProfileContext';
import {
  PackageCheck,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarPlus,
  RefreshCw,
  Circle,
  ListChecks,
  Check,
  Users,
  DollarSign,
  Zap,
  ChevronsDown,
  ChevronsUp,
  Clock,
  Upload,
  FileText,
  Paperclip,
  Copy,
  Star,
  Minus,
  Plus,
  Trash2,
  Layers,
  Lock,
  Download,
  Mail,
  Phone,
} from 'lucide-react';
import {
  getMoveInChecklist,
  toggleMoveInChecklistItem,
  getMoveInSlotsForDates,
  bookMoveInSlot,
  confirmMoveInScheduledDate,
  getMoveInTenantsInsurance,
  uploadInsuranceDocument,
  deleteInsuranceDocument,
  uploadUtilitiesDocument,
  deleteUtilitiesDocument,
  uploadUtilitiesEnrollmentForm,
  deleteUtilitiesEnrollmentForm,
  getMoveInUtilitiesStatus,
  getMoveInRentSplit,
  getMoveInUnitRoommates,
  saveMoveInRentSplit,
  getAccessoryInsuranceDocs,
  uploadAccessoryInsuranceDoc,
  deleteAccessoryInsuranceDoc,
  getMoveInContent,
  MoveInChecklistItem,
  MoveInTenantInsurance,
  MoveInSlotInfo,
  MoveInUtilitiesStatus,
  MoveInRentSplitData,
  MoveInUnitRoommatesData,
  AccessoryInsuranceDoc,
  MoveInContentItem,
} from '../services/tenantApi';
import { PdfViewer } from '../components/PdfViewer';
import { Johnson390MoveInAppointmentBooking } from '../components/move-in/Johnson390MoveInAppointmentBooking';
import { MoveInAppointmentBookingFlow } from '../components/move-in/MoveInAppointmentBookingFlow';
import { buildMondaySundayWeek, JOHNSON_390_MOVE_IN_BLOCKED_DATES_ET } from '../components/move-in/johnson390MoveInSlots';
import {
  formatConfirmedDateISO,
  formatConfirmedDayOfWeek,
  formatConfirmedTime,
  formatFullDate,
  formatSlotTime,
} from '../components/move-in/moveInAppointmentFormatters';
import { downloadMoveInICS } from '../components/move-in/moveInAppointmentIcs';
import { MOVE_IN_CHECKLIST_TEMPLATE } from '../config/move-in-checklist-template';
import { JOHNSON_390_PROPERTY_ID } from '../config/hive';
import {
  formatPhoneForTenantPortalDisplay,
  phoneCopyValue,
} from '../utils/phone-display';

const CUM_UTILITY_ENROLLMENT_SECTION = 'cum-utility-enrollment-form';
const CUM_BILLING_EMAIL = 'billing@canadautility.ca';
const UTILITIES_ENROLLMENT_FORM_ITEM_KEY = 'utilities-enrollment-form-uploaded';
const ROOMMATES_INTRODUCED_ITEM_KEY = 'roommates-introduced';

// ---------------------------------------------------------------------------
// Date helpers (Eastern Time, date-only -- no timezone conversion of dates)
// ---------------------------------------------------------------------------

function getEasternToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

const MOVE_IN_BOOKING_OPEN_DATE = new Date('2026-08-01T14:00:00Z'); // Aug 1, 2026 10:00 AM EDT

function isMoveInBookingOpen(): boolean {
  return new Date() >= MOVE_IN_BOOKING_OPEN_DATE;
}

function getMoveInBookingCountdown(): { days: number; hours: number; minutes: number } | null {
  const now = new Date();
  const diff = MOVE_IN_BOOKING_OPEN_DATE.getTime() - now.getTime();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { days, hours, minutes };
}

function buildMoveInDateWindow(anchorDate: string, weekOffset: number = 0, scheduledDate?: string | null): string[] {
  const [y, m, d] = anchorDate.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  start.setDate(start.getDate() + weekOffset * 7);

  const schedET = scheduledDate
    ? new Date(scheduledDate).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
    : null;
  const isMay1or2 = schedET?.endsWith('-05-01') || schedET?.endsWith('-05-02');
  const schedYear = schedET ? parseInt(schedET.slice(0, 4), 10) : null;
  const may1 = schedYear ? `${schedYear}-05-01` : null;
  const may2 = schedYear ? `${schedYear}-05-02` : null;
  const may3 = schedYear ? `${schedYear}-05-03` : null;
  const sep1 = schedYear ? `${schedYear}-09-01` : null;

  const dates: string[] = [];
  let cursor = new Date(start);
  let attempts = 0;
  while (dates.length < 7 && attempts < 60) {
    const str = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
    attempts++;

    if (isMay1or2 && sep1 && str > sep1) break;

    let valid = true;
    if (isMay1or2) {
      if (str === may1 || str === may2 || str === may3) {
        valid = true;
      } else {
        const dayOfWeek = cursor.getDay();
        valid = dayOfWeek >= 1 && dayOfWeek <= 5;
      }
    }

    if (valid) dates.push(str);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

// ---------------------------------------------------------------------------
// Section definitions (drives both the tracker and the page section IDs)
// ---------------------------------------------------------------------------

interface PageSection {
  id: string;
  label: string;
  step?: number;
}

const PAGE_SECTIONS: PageSection[] = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'book-appointment', label: 'Book Your Appointment', step: 1 },
  { id: 'insurance', label: 'Tenant Insurance', step: 2 },
  { id: 'unit-roommates', label: 'Your Unit Roommates', step: 3 },
  { id: 'utilities', label: 'Utility Account Confirmation', step: 4 },
  { id: 'rent-split', label: 'Rent Split', step: 5 },
];

// ---------------------------------------------------------------------------
// Section scroll tracker
// ---------------------------------------------------------------------------

function getScrollContainer(): HTMLElement | Window {
  return document.querySelector('main') ?? window;
}

function getScrollTop(): number {
  const container = getScrollContainer();
  return container instanceof Window ? container.scrollY : container.scrollTop;
}

function smoothScrollTo(top: number) {
  const container = getScrollContainer();
  container.scrollTo({ top, behavior: 'smooth' });
}

function SectionTracker({
  sections,
  activeId,
}: {
  sections: PageSection[];
  activeId: string | null;
}) {
  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const container = getScrollContainer();
    const containerTop = container instanceof Window ? 0 : container.getBoundingClientRect().top;
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-h')) || 0;
    const y = el.getBoundingClientRect().top - containerTop + getScrollTop() - headerHeight - 16;
    smoothScrollTo(y);
  };

  return (
    <nav className="hidden 2xl:flex flex-col items-center gap-0 fixed top-1/2 -translate-y-1/2 right-6 z-30">
      {sections.map((section, i) => {
        const isActive = section.id === activeId;
        const isLast = i === sections.length - 1;

        return (
          <div key={section.id} className="flex flex-col items-center">
            {/* Dot + label row */}
            <button
              onClick={() => handleClick(section.id)}
              className="flex items-center gap-2 group relative"
              title={section.label}
            >
              {/* Label (shows on hover or when active) */}
              <span
                className={`text-[11px] font-medium whitespace-nowrap transition-all duration-200 pr-1 ${
                  isActive
                    ? 'text-red-700 opacity-100'
                    : 'text-gray-500 opacity-100'
                }`}
              >
                {section.label}
              </span>

              {/* Dot or step number */}
              {section.step ? (
                <span
                  className={`rounded-full flex items-center justify-center text-[9px] font-bold transition-all duration-200 flex-shrink-0 ${
                    isActive
                      ? 'w-5 h-5 bg-red-700 text-white border-2 border-red-700 shadow-sm'
                      : 'w-5 h-5 bg-white text-gray-400 border-2 border-gray-300 group-hover:border-gray-400'
                  }`}
                >
                  {section.step}
                </span>
              ) : (
                <span
                  className={`rounded-full border-2 transition-all duration-200 flex-shrink-0 ${
                    isActive
                      ? 'w-3.5 h-3.5 bg-red-700 border-red-700 shadow-sm'
                      : 'w-2.5 h-2.5 bg-white border-gray-300 group-hover:border-gray-400'
                  }`}
                />
              )}
            </button>

            {/* Connector line */}
            {!isLast && (
              <div className="w-px h-6 bg-gray-200 my-1" />
            )}
          </div>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Task summary (checklist overview with scroll-to-section)
// ---------------------------------------------------------------------------

const TASK_SECTION_MAP: Record<string, { sectionId: string; step: number }> = {
  'appointment-booked': { sectionId: 'book-appointment', step: 1 },
  'all-insurance-complete': { sectionId: 'insurance', step: 2 },
  'roommates-introduced': { sectionId: 'unit-roommates', step: 3 },
  'utilities-enrollment-form-uploaded': { sectionId: 'utilities', step: 4 },
  'utilities-confirmed': { sectionId: 'utilities', step: 4 },
  'rent-split-confirmed': { sectionId: 'rent-split', step: 5 },
};

function scrollToSection(sectionId: string) {
  const el = document.getElementById(sectionId);
  if (!el) return;
  const container = getScrollContainer();
  const containerTop = container instanceof Window ? 0 : container.getBoundingClientRect().top;
  const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-h')) || 0;
  const y = el.getBoundingClientRect().top - containerTop + getScrollTop() - headerH - 24;
  smoothScrollTo(y);
}

interface TaskSummaryItem {
  itemKey: string;
  label: string;
  sortOrder: number;
  completed: boolean;
}

function TaskSummary({ items, onNavigate }: { items: TaskSummaryItem[]; onNavigate?: (sectionId: string) => void }) {
  if (items.length === 0) return null;

  const completed = items.filter((i) => i.completed).length;
  const total = items.length;
  const allDone = completed === total;

  const handleClick = (sectionId: string) => {
    if (onNavigate) {
      onNavigate(sectionId);
    } else {
      scrollToSection(sectionId);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-white/10 overflow-hidden">
      <div className="px-5 pt-5 pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ListChecks size={20} className="text-red-700 dark:text-red-400 shrink-0" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Summary of Tasks</h3>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
            allDone
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {completed} / {total}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-out ${allDone ? 'bg-green-600' : 'bg-red-600'}`}
            style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="px-3 pb-3">
        {items
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((item, idx) => {
            const taskNum = idx + 1;
            const mapping = TASK_SECTION_MAP[item.itemKey];
            return (
              <button
                key={item.itemKey}
                onClick={() => mapping && handleClick(mapping.sectionId)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-left group"
              >
                {item.completed ? (
                  <CheckCircle2 size={18} className="text-green-600 shrink-0" />
                ) : (
                  <Circle size={18} className="text-red-400 shrink-0 group-hover:text-red-500 transition-colors" />
                )}
                <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                  item.completed ? 'text-green-500' : 'text-gray-400'
                }`}>
                  Task {taskNum}
                </span>
                <span className={`text-sm flex-1 ${
                  item.completed ? 'text-gray-400 line-through' : 'text-gray-800'
                }`}>
                  {item.label}
                </span>
              </button>
            );
          })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function useHeaderHeight() {
  useEffect(() => {
    const header = document.querySelector('[data-sticky-header]');
    if (!header) return;

    const update = () => {
      document.documentElement.style.setProperty(
        '--sticky-header-h',
        `${header.getBoundingClientRect().height}px`
      );
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);
}

function CollapsibleSection({
  number,
  title,
  badge,
  isComplete,
  defaultOpen,
  informational,
  forceToggle,
  sectionId,
  forceOpenId,
  children,
}: {
  number: number;
  title: string;
  badge?: string;
  isComplete: boolean;
  defaultOpen?: boolean;
  /** No checklist task — neutral header, open by default, no Complete badge */
  informational?: boolean;
  forceToggle?: { open: boolean; rev: number };
  sectionId?: string;
  forceOpenId?: { sectionId: string; rev: number };
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(
    defaultOpen ?? (informational ? true : !isComplete),
  );

  const prevRev = useRef(forceToggle?.rev ?? 0);
  useEffect(() => {
    if (forceToggle && forceToggle.rev !== prevRev.current) {
      prevRev.current = forceToggle.rev;
      setOpen(forceToggle.open);
    }
  }, [forceToggle?.rev, forceToggle?.open]);

  const prevOpenRev = useRef(forceOpenId?.rev ?? 0);
  useEffect(() => {
    if (forceOpenId && sectionId && forceOpenId.rev !== prevOpenRev.current) {
      prevOpenRev.current = forceOpenId.rev;
      if (forceOpenId.sectionId === sectionId) {
        setOpen(true);
      }
    }
  }, [forceOpenId?.rev, forceOpenId?.sectionId, sectionId]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-white/10">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`sticky z-10 w-full px-5 py-4 bg-white/95 backdrop-blur-sm text-left transition-colors rounded-t-2xl ${open ? 'border-b border-gray-100' : 'rounded-b-2xl'}`}
        style={{ top: 'var(--sticky-header-h, 0px)' }}
      >
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold flex-shrink-0 transition-colors ${
            informational
              ? 'bg-slate-600'
              : isComplete
                ? 'bg-green-600'
                : 'bg-red-700'
          }`}>
            {informational || !isComplete ? number : <Check size={14} />}
          </div>
          <span className={`text-xs font-semibold uppercase tracking-wider transition-colors ${
            informational ? 'text-gray-600' : isComplete ? 'text-green-700' : 'text-gray-500'
          }`}>{title}</span>
          {badge && <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-full px-2 py-0.5">{badge}</span>}
          {isComplete && !informational && (
            <span className="text-[10px] font-medium text-green-600 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-full px-2 py-0.5">Complete</span>
          )}
          <div className="flex-1" />
          <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <div className={`px-5 pt-5 pb-6 space-y-5 ${open ? '' : 'hidden'}`}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAQ Accordion
// ---------------------------------------------------------------------------

interface FaqItem {
  question: string;
  answer: string;
  category?: string;
}

const MOVE_IN_FAQ: FaqItem[] = [
  {
    question: 'What is a move-in appointment?',
    answer: 'A move-in appointment is when you meet with an Amber Peak representative to receive your keys and unit codes, do a walkthrough of your unit, and document its condition. At the end of your appointment, possession of the unit will officially be yours for the duration of your lease.',
  },
  {
    question: 'Will I be given access to my unit without completing all move-in steps?',
    answer: 'No. All required move-in steps must be completed prior to your move-in appointment. Amber Peak will not provide access to the unit until all of these steps have been completed.',
  },
  {
    question: 'When is the first time I can get access to the property?',
    answer: 'As per your lease, your unit will be provided to you at the earliest on your lease start time and move-in appointment time, as outlined in the supplemental additional terms and conditions appended to your lease. This is the first availability. We can also accommodate later times if that is necessary.',
  },
];

const INSURANCE_FAQ: FaqItem[] = [
  // --- Acquiring & Managing Your Policy ---
  {
    category: 'Acquiring & Managing Your Policy',
    question: 'Does one tenant buying a policy cover everyone on the lease?',
    answer:
      'No. A policy only covers the individuals who are named insured on it. A single policy can have multiple named insureds, so you may purchase one policy and add all housemates to it. Alternatively, each tenant can obtain their own individual policy. Either way, every tenant on the lease must be covered.',
  },
  {
    question: 'How do I submit proof of insurance to Amber Peak?',
    answer:
      'Upload it to the move-in portal above. After you have uploaded the policy, Amber Peak will review it and ensure that it complies with our requirements.',
  },
  // --- Understanding Your Coverage ---
  {
    category: 'Understanding Your Coverage',
    question: "What is a deductible, and when do I have to pay it?",
    answer:
      'A deductible is the amount you pay out of pocket before your insurance kicks in. For example, if you have a $500 deductible and file a claim for $3,000 in stolen belongings, your insurer will pay $2,500 and you cover the first $500. You pay the deductible each time you make a claim. Choose a deductible amount you can comfortably afford in an emergency.',
  },
  {
    question: 'Will my premium go up if I make a claim?',
    answer:
      'It can. Filing a claim, especially multiple claims, may result in a higher premium at renewal. For smaller losses that are close to your deductible amount, it may be worth weighing whether filing a claim is cost-effective. For example, if your deductible is $500 and the loss is $600, the $100 payout may not be worth a potential premium increase. For significant losses, always file a claim.',
  },
  {
    question: 'Is my laptop covered if it is stolen from my car or a coffee shop?',
    answer:
      'In most cases, yes. Tenant insurance typically covers your personal belongings wherever they are, not just inside your unit. This means theft from your car, a coffee shop, or while travelling may be covered, subject to your policy\'s terms and deductible. Check your specific policy for any exclusions or limits on off-premises coverage.',
  },
  {
    question: "Does my insurance cover my roommate's belongings?",
    answer:
      'No, unless your roommate is specifically named on your policy. Each person on the lease should either be named on a shared policy or have their own individual policy. If a loss occurs, only the belongings of people named on the policy will be covered.',
  },
  {
    question: 'I have expensive items like jewelry or a high-end computer. Is there a limit on how much I can claim?',
    answer:
      'Yes. Most policies have "sub-limits" for certain categories like jewelry, electronics, and collectibles. For example, your policy might cap jewelry claims at $2,000–$5,000 even if your overall coverage is much higher. If you own high-value items, ask your insurer about adding a "floater" or "endorsement" to increase coverage for those specific items.',
  },
  {
    question: "If the landlord's pipe bursts and ruins my furniture, doesn't the landlord have to pay for it?",
    answer:
      "No. This is one of the most common misconceptions. The landlord's insurance covers the physical structure of the building, including pipes, walls, and floors. It does not cover your personal belongings. If a burst pipe damages your furniture, clothing, or electronics, your tenant insurance is what covers the replacement cost. This is a key reason why tenant insurance is required.",
  },
  {
    question: 'Am I responsible for repairing the property if there is a sewer backup or overland water event?',
    answer:
      "No. The property owner's insurance covers repairs to the building. However, you want sewer backup and overland water coverage on your own policy so that your personal belongings and any temporary displacement costs are covered.",
  },
  {
    question: 'My fridge stopped working. Is my landlord responsible for my spoiled food?',
    answer:
      'Appliance malfunctions can occur without warning and are beyond our ability to prevent entirely. Consequential losses that result from a malfunctioning fridge, such as spoiled food, fall outside the scope of what we can control as a property manager. Many tenant insurance policies include a provision for food spoilage, though keep in mind that your deductible still applies. If the value of spoiled food is less than your deductible, a claim may not result in a payout. Notify Amber Peak in writing if the fridge is not functioning properly on first occurrence. We will seek to have the appliance repaired or replaced as soon as reasonably possible.',
  },
  {
    question: 'The washer or dryer damaged my clothing. Is my landlord responsible?',
    answer:
      'Appliance malfunctions can occur without warning and are beyond our ability to prevent entirely. Consequential losses that result from malfunctioning washers and dryers, such as damaged clothing, fall outside the scope of what we can control as a property manager. If your clothing is damaged, your tenant insurance policy may cover the loss, subject to your deductible. Notify Amber Peak in writing if the appliance is not functioning properly and has damaged possessions on first occurrence. We will seek to have the appliance repaired or replaced as soon as reasonably possible.',
  },
  // --- Emergency & Claims ---
  {
    category: 'Emergency & Claims',
    question: 'Our home was broken into. What do I do?',
    answer:
      'First, ensure everyone is safe and call the police to file a report. Do not touch or clean up anything until officers have documented the scene. Then contact your insurance provider as soon as possible to open a claim. Take photos and video of all damage and any items that are missing or broken. Keep a copy of the police report number as your insurer will need it. Once your insurance claim has been initiated, you may need to provide further information as you wait for your claim to be processed.',
  },
  {
    question: 'My laptop was stolen from my home during a break-in. What do I do now?',
    answer:
      'File a police report and contact your insurance provider to open a claim. Provide any receipts, serial numbers, or proof of purchase you have for the stolen item. Your personal belongings coverage should cover theft, subject to your deductible and any sub-limits on electronics.',
  },
  {
    question: 'Our basement flooded and damaged our belongings. How do I handle this?',
    answer:
      'Amber Peak will begin remediation work, either with third-party contractors or through the best means available, to restore the unit to its original condition. Document all damage to your personal belongings with photos and video before moving or discarding anything, and contact your insurance provider to file a claim. If the unit becomes uninhabitable during repairs, you will need to seek temporary accommodations. Keep all receipts and records of these expenses, as you will be able to claim them under the additional living expenses provision of your tenant insurance policy.',
  },
  {
    question: 'We had a small kitchen fire. Where should I stay while damages are repaired?',
    answer:
      'If your unit is temporarily uninhabitable, your tenant insurance\'s "additional living expenses" coverage can help pay for a hotel, short-term rental, or other temporary housing while repairs are completed. Contact your insurer right away to confirm your coverage and get guidance on approved expenses. Keep all receipts for temporary housing, meals, and other extra costs as you will need them for your claim.',
  },
  {
    question: 'Our unit has suffered major fire damage. How do we proceed?',
    answer:
      'Ensure everyone is safe and contact emergency services. Once you are safe, notify Amber Peak of the damage and begin preparing the information you will need to send to your insurance provider. Contact your insurance provider as soon as possible to open a claim. Confirm your additional living expenses coverage to arrange temporary accommodations while the unit is being restored. Document everything: take photos, keep receipts, and make a detailed list of damaged or destroyed belongings. Your insurer will use this documentation to process your claim and determine your payout.',
  },
  // --- Liability & Legal ---
  {
    category: 'Liability & Legal',
    question: 'Someone was seriously injured at our unit. What should I do?',
    answer:
      'Call emergency services immediately and ensure the injured person receives medical attention. Once the situation is stabilized, document what happened, including the date, time, circumstances, and any witness contact information. If a claim were to arise from the incident, your personal liability coverage is designed to help cover medical expenses and legal defense costs. Contact your insurance provider to report the incident so they are aware and can advise you on next steps.',
  },
  {
    question: "I've been served with an injury lawsuit. How do I proceed?",
    answer:
      'Contact your insurance provider immediately. Do not delay. Your liability coverage typically includes legal defense costs. Your insurer will assign a claims adjuster and may provide legal representation. Do not respond to the lawsuit, admit fault, or communicate directly with the other party\'s lawyer until your insurer has been notified and has provided guidance.',
  },
  {
    question: 'What happens if I accidentally cause damage to the building, for example, leaving a tap running and it leaks into the unit below?',
    answer:
      'This is exactly what the liability portion of your tenant insurance covers. The property owner\'s insurance company may come after you for the cost of repairs to the building and the affected unit. Your liability coverage can protect you from having to pay these costs out of pocket and can cover legal defense if needed.',
  },
];

function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  let lastCategory: string | undefined;

  return (
    <div>
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        const showCategory = item.category && item.category !== lastCategory;
        if (item.category) lastCategory = item.category;

        return (
          <div key={i}>
            {showCategory && (
              <div className={`text-xs font-bold text-gray-500 uppercase tracking-wider ${i === 0 ? 'pt-2' : 'pt-4 mt-2 border-t border-gray-200'} pb-1`}>
                {item.category}
              </div>
            )}
            <div className="border-b border-gray-100 dark:border-white/5 last:border-b-0">
              <button
                onClick={() => setOpenIndex(isOpen ? null : i)}
                className="w-full flex items-center justify-between py-3 text-left group"
              >
                <span className={`text-sm font-medium pr-4 ${isOpen ? 'text-red-700' : 'text-gray-800 group-hover:text-red-700'}`}>
                  {item.question}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isOpen && (
                <div className="pb-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
                  {item.answer}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Appointment booking gate UI
// ---------------------------------------------------------------------------

function MoveInBookingNotYetOpenPanel({
  countdown,
  johnson390Default,
}: {
  countdown: { days: number; hours: number; minutes: number } | null;
  johnson390Default?: { startTime: string; endTime: string | null } | null;
}) {
  const defaultTimeLabel = johnson390Default
    ? johnson390Default.endTime && johnson390Default.endTime !== johnson390Default.startTime
      ? formatConfirmedTime(johnson390Default.startTime, johnson390Default.endTime)
      : `${formatSlotTime(johnson390Default.startTime)} ET`
    : null;

  return (
    <div className="text-center py-10 space-y-4">
      <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
        <Lock size={28} className="text-amber-600" />
      </div>
      <div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Appointment Booking Not Yet Available</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Move-in appointment booking will go live on
        </p>
        <p className="text-base font-semibold text-gray-900 dark:text-white mt-1">
          Saturday, August 1 at 10:00 AM ET
        </p>
      </div>
      {countdown && (
        <div className="flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400">
          <Clock size={14} className="shrink-0" />
          <span>
            {countdown.days > 0 && `${countdown.days}d `}
            {countdown.hours}h {countdown.minutes}m remaining
          </span>
        </div>
      )}

      {johnson390Default && defaultTimeLabel && (
        <div className="max-w-md mx-auto text-left space-y-4 pt-2">
          <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 dark:border-white/10">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white">Your default move-in appointment</h5>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Every tenant at 390 Johnson Street has been assigned this time.
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              <div className="grid grid-cols-[72px_1fr] px-5 py-2.5 gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date</span>
                <span className="text-sm text-gray-900 dark:text-white">{formatConfirmedDateISO(johnson390Default.startTime)}</span>
              </div>
              <div className="grid grid-cols-[72px_1fr] px-5 py-2.5 gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Day</span>
                <span className="text-sm text-gray-900 dark:text-white">{formatConfirmedDayOfWeek(johnson390Default.startTime)}</span>
              </div>
              <div className="grid grid-cols-[72px_1fr] px-5 py-2.5 gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Time</span>
                <span className="text-sm text-gray-900 dark:text-white">{defaultTimeLabel}</span>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2.5 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-xl">
            <AlertCircle size={16} className="text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-950 leading-relaxed">
              On <span className="font-semibold">Saturday, August 1 at 10:00 AM ET</span>, you will be able to book an earlier move-in appointment on September 1st or choose a move-in time after September 1st. Until then, your default appointment above remains in place.
            </p>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm mx-auto">
        Appointments are booked on a first-come, first-served basis. Please check back at the time above to select your slot.
      </p>
    </div>
  );
}

function CumUtilityNumberedStep({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <div className="flex gap-2.5">
      <div className="w-5 h-5 rounded-full bg-red-700 text-white text-[11px] font-bold leading-[20px] text-center shrink-0 mt-[1px]">
        {number}
      </div>
      <div className="text-sm text-gray-700 dark:text-gray-300 leading-5">{children}</div>
    </div>
  );
}

function CopyContactRow({
  icon: Icon,
  displayValue,
  copyValue,
  copyFieldId,
  copiedFieldId,
  onCopy,
  emptyLabel,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  displayValue: string | null;
  copyValue: string | null;
  copyFieldId: string;
  copiedFieldId: string | null;
  onCopy: (fieldId: string, value: string) => void;
  emptyLabel: string;
}) {
  if (!displayValue) {
    return (
      <p className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
        <Icon size={15} className="shrink-0" />
        {emptyLabel}
      </p>
    );
  }
  const copied = copiedFieldId === copyFieldId;
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon size={15} className="text-gray-400 dark:text-gray-500 shrink-0 mt-0.5" />
      <span className="flex-1 min-w-0 break-all text-gray-800 dark:text-gray-100">{displayValue}</span>
      {copyValue ? (
        <button
          type="button"
          onClick={() => onCopy(copyFieldId, copyValue)}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 shrink-0"
        >
          {copied ? (
            <>
              <CheckCircle2 size={12} />
              Copied
            </>
          ) : (
            <>
              <Copy size={12} />
              Copy
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}

interface UtilitiesDocumentUploadCardProps {
  stepNumber?: number;
  title: string;
  description: string;
  hasDocument: boolean;
  documentFilename?: string | null;
  documentUrl?: string | null;
  showStaffReviewStatus?: boolean;
  staffReviewConfirmed?: boolean;
  uploading: boolean;
  deleting: boolean;
  dragOver: boolean;
  onDragOverChange: (over: boolean) => void;
  onUpload: (file: File) => void;
  onDelete: () => void;
  onBrowseClick: () => void;
  uploadError?: string | null;
  dropzonePrompt: React.ReactNode;
}

function UtilitiesDocumentUploadCard({
  stepNumber,
  title,
  description,
  hasDocument,
  documentFilename,
  documentUrl,
  showStaffReviewStatus = false,
  staffReviewConfirmed = false,
  uploading,
  deleting,
  dragOver,
  onDragOverChange,
  onUpload,
  onDelete,
  onBrowseClick,
  uploadError,
  dropzonePrompt,
}: UtilitiesDocumentUploadCardProps) {
  const uploadedTone = showStaffReviewStatus
    ? staffReviewConfirmed
    : true;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-white/5">
        {stepNumber != null && (
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full bg-red-700 text-white text-[11px] font-bold leading-[20px] text-center shrink-0">
              {stepNumber}
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
              Step {stepNumber}
            </span>
          </div>
        )}
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h4>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>
      </div>
      <div className="px-4 py-3 space-y-3">
        {hasDocument ? (
          <div className="space-y-3">
            <div className={`flex items-center gap-2 rounded-md px-3 py-2.5 ${
              uploadedTone
                ? 'bg-green-50 border border-green-200'
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <Paperclip size={14} className={uploadedTone ? 'text-green-600 shrink-0' : 'text-amber-600 shrink-0'} />
              <span className={`text-sm font-medium truncate flex-1 ${uploadedTone ? 'text-green-900' : 'text-amber-900'}`}>
                {documentFilename || title}
              </span>
              {showStaffReviewStatus && (
                <span className={`text-[10px] font-medium shrink-0 ${uploadedTone ? 'text-green-600' : 'text-amber-600'}`}>
                  {staffReviewConfirmed ? 'Confirmed' : 'Pending review'}
                </span>
              )}
              {documentUrl && (
                <>
                  <span className={uploadedTone ? 'text-green-300 shrink-0' : 'text-amber-300 shrink-0'}>|</span>
                  <a
                    href={documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-xs font-semibold shrink-0 ${uploadedTone ? 'text-green-700 hover:text-green-900' : 'text-amber-700 hover:text-amber-900'}`}
                  >
                    View
                  </a>
                </>
              )}
              <span className={uploadedTone ? 'text-green-300 shrink-0' : 'text-amber-300 shrink-0'}>|</span>
              <button
                type="button"
                onClick={onBrowseClick}
                disabled={uploading || deleting}
                className={`text-xs font-semibold shrink-0 ${uploadedTone ? 'text-green-700 hover:text-green-900' : 'text-amber-700 hover:text-amber-900'}`}
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : 'Replace'}
              </button>
              <span className={uploadedTone ? 'text-green-300 shrink-0' : 'text-amber-300 shrink-0'}>|</span>
              <button
                type="button"
                onClick={onDelete}
                disabled={uploading || deleting}
                className="text-xs font-semibold shrink-0 text-red-600 dark:text-red-400 hover:text-red-800"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </button>
            </div>
            {showStaffReviewStatus && !staffReviewConfirmed && (
              <p className="text-xs text-gray-500 dark:text-gray-400">Amber Peak will review your document shortly.</p>
            )}
          </div>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOverChange(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); onDragOverChange(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); onDragOverChange(false); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDragOverChange(false);
              const file = e.dataTransfer.files?.[0];
              if (file) onUpload(file);
            }}
            onClick={() => { if (!uploading) onBrowseClick(); }}
            className={`relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer ${
              uploading
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                : dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/30'
            }`}
          >
            {uploading ? (
              <>
                <Loader2 size={28} className="text-blue-500 animate-spin" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Uploading your document...</p>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Upload size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{dropzonePrompt}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">PDF, JPEG, or PNG. Max 10 MB.</p>
                </div>
              </>
            )}
          </div>
        )}

        {uploadError && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 px-3 py-2">
            <AlertCircle size={16} className="text-red-500 shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-400">{uploadError}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CumUtilityEnrollmentFormPanel({ document }: { document: MoveInContentItem | undefined }) {
  if (!document?.cloudfrontUrl) {
    return (
      <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
        <AlertCircle size={16} className="text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-950 leading-relaxed">
          The utility billing enrollment form is not available yet. Please contact your property manager if you need a copy.
        </p>
      </div>
    );
  }

  const filename = document.s3Key?.split('/').pop() ?? 'Utility-Billing-Enrollment-Form.pdf';
  const pdfViewerUrl = document.viewerUrl ?? document.cloudfrontUrl;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-white/5">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">Utility Billing Enrollment Form</h4>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-white/5">
        <a
          href={document.cloudfrontUrl}
          target="_blank"
          rel="noopener noreferrer"
          download={filename}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
        >
          <Download size={14} />
          Download PDF
        </a>
        <a
          href={document.cloudfrontUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-blue-700 hover:text-blue-900 transition-colors"
        >
          <FileText size={14} />
          Open in new tab
        </a>
      </div>
      <div className="h-[min(70vh,520px)]">
        <PdfViewer url={pdfViewerUrl} />
      </div>
    </div>
  );
}

export default function MoveInPage() {
  const { tenant, loading: profileLoading, refreshProfile } = useTenantProfile();
  const getToken = useTenantApiToken();
  useHeaderHeight();

  const moveIn = tenant?.moveIn ?? null;
  const isByTheBed = moveIn?.isByTheBed ?? tenant?.residentialLease?.isByTheBed ?? false;
  /** By-the-bed tenants act as sole leaseholder in the portal; "House Manager" reads confusing. */
  const showHouseManagerLabel = !isByTheBed;

  const showUtilitiesSection = moveIn?.showUtilitiesSection ?? false;
  const showRentSplitSection = moveIn?.showRentSplitSection ?? false;

  const [sectionToggle, setSectionToggle] = useState<{ open: boolean; rev: number }>({ open: false, rev: 0 });
  const [forceOpenId, setForceOpenId] = useState<{ sectionId: string; rev: number }>({ sectionId: '', rev: 0 });
  const expandAll = useCallback(() => setSectionToggle((s) => ({ open: true, rev: s.rev + 1 })), []);
  const collapseAll = useCallback(() => setSectionToggle((s) => ({ open: false, rev: s.rev + 1 })), []);

  // --- Data state ---
  const [checklistItems, setChecklistItems] = useState<MoveInChecklistItem[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(true);
  const [insuranceData, setInsuranceData] = useState<{ tenants: MoveInTenantInsurance[]; allComplete: boolean; currentUserTenantId: number | null } | null>(null);
  const [utilitiesData, setUtilitiesData] = useState<MoveInUtilitiesStatus | null>(null);
  const [moveInContent, setMoveInContent] = useState<MoveInContentItem[]>([]);
  const [rentSplitData, setRentSplitData] = useState<MoveInRentSplitData | null>(null);
  const [slotsByDate, setSlotsByDate] = useState<Record<string, MoveInSlotInfo[]>>({});

  // --- Appointment mode and calendar state ---
  const [mode, setMode] = useState<'confirmed' | 'picker' | 'pending-confirm'>('confirmed');
  const [confirming, setConfirming] = useState(false);
  const [rescheduleFromStart, setRescheduleFromStart] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [optimisticAppointment, setOptimisticAppointment] = useState<{
    startTime: string;
    endTime: string;
  } | null>(null);
  const [bookingOpen, setBookingOpen] = useState(isMoveInBookingOpen);
  const [countdown, setCountdown] = useState(getMoveInBookingCountdown);

  const appointmentStartTime = optimisticAppointment?.startTime ?? moveIn?.appointmentStartTime ?? null;
  const appointmentEndTime = optimisticAppointment?.endTime ?? moveIn?.appointmentEndTime ?? null;

  useEffect(() => {
    if (bookingOpen) return;
    const timer = setInterval(() => {
      if (isMoveInBookingOpen()) {
        setBookingOpen(true);
        setCountdown(null);
        clearInterval(timer);
      } else {
        setCountdown(getMoveInBookingCountdown());
      }
    }, 30_000);
    return () => clearInterval(timer);
  }, [bookingOpen]);

  // Insurance upload
  const [insuranceUploading, setInsuranceUploading] = useState(false);
  const [insuranceUploadError, setInsuranceUploadError] = useState<string | null>(null);
  const [insuranceDragOver, setInsuranceDragOver] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [utilitiesEmailCopied, setUtilitiesEmailCopied] = useState(false);
  const [utilitiesToCopied, setUtilitiesToCopied] = useState(false);
  const [utilitiesSubjectCopied, setUtilitiesSubjectCopied] = useState(false);
  const [utilitiesUploading, setUtilitiesUploading] = useState(false);
  const [utilitiesUploadError, setUtilitiesUploadError] = useState<string | null>(null);
  const [utilitiesDragOver, setUtilitiesDragOver] = useState(false);
  const [utilitiesDeleting, setUtilitiesDeleting] = useState(false);
  const [enrollmentUploading, setEnrollmentUploading] = useState(false);
  const [enrollmentUploadError, setEnrollmentUploadError] = useState<string | null>(null);
  const [enrollmentDragOver, setEnrollmentDragOver] = useState(false);
  const [enrollmentDeleting, setEnrollmentDeleting] = useState(false);
  const insuranceFileRef = useRef<HTMLInputElement>(null);
  const utilitiesFileRef = useRef<HTMLInputElement>(null);
  const enrollmentFileRef = useRef<HTMLInputElement>(null);
  const accessoryInsFileRef = useRef<HTMLInputElement>(null);

  // Accessory insurance documents
  const [accessoryDocs, setAccessoryDocs] = useState<AccessoryInsuranceDoc[]>([]);
  const [accessoryLoading, setAccessoryLoading] = useState(false);
  const [accessoryUploading, setAccessoryUploading] = useState(false);
  const [accessoryUploadError, setAccessoryUploadError] = useState<string | null>(null);
  const [accessoryDragOver, setAccessoryDragOver] = useState(false);
  const [accessoryDeleting, setAccessoryDeleting] = useState<string | null>(null);

  // Rent split editing
  const [rentSplitEdits, setRentSplitEdits] = useState<Record<number, string>>({});
  const [rentSplitSaving, setRentSplitSaving] = useState(false);
  const [unitRoommates, setUnitRoommates] = useState<MoveInUnitRoommatesData | null>(null);
  const [unitRoommatesLoading, setUnitRoommatesLoading] = useState(false);
  const [copiedRoommateField, setCopiedRoommateField] = useState<string | null>(null);

  const handleCopyRoommateField = useCallback((fieldId: string, value: string) => {
    void navigator.clipboard.writeText(value);
    setCopiedRoommateField(fieldId);
    window.setTimeout(() => setCopiedRoommateField(null), 2000);
  }, []);

  const unitRoommatesSorted = useMemo(() => {
    if (!unitRoommates?.roommates.length) return [];
    const bedOrder = (label: string) => {
      const match = label.match(/\d+/);
      return match ? parseInt(match[0], 10) : 9999;
    };
    return [...unitRoommates.roommates].sort(
      (a, b) => bedOrder(a.bedLabel) - bedOrder(b.bedLabel),
    );
  }, [unitRoommates?.roommates]);

  // --- Parallel data fetch ---
  const moveInFetchedRef = useRef(false);
  const moveInContentFetchedRef = useRef(false);
  useEffect(() => {
    if (!moveIn?.id) return;
    if (moveInContentFetchedRef.current) return;
    moveInContentFetchedRef.current = true;
    getMoveInContent(getToken)
      .then(setMoveInContent)
      .catch(() => setMoveInContent([]));
  }, [moveIn?.id, getToken]);

  useEffect(() => {
    if (!moveIn?.id) return;
    if (moveInFetchedRef.current) return;
    moveInFetchedRef.current = true;

    (async () => {
      const token = await getToken();
      const withToken = () => Promise.resolve(token);
      const id = moveIn.id;

      const fetchTasks = await Promise.allSettled([
        getMoveInChecklist(withToken, id),
        getMoveInTenantsInsurance(withToken, id),
        ...(moveIn.showUtilitiesSection !== false ? [getMoveInUtilitiesStatus(withToken, id)] : []),
        ...(moveIn.showRentSplitSection !== false ? [getMoveInRentSplit(withToken, id)] : []),
        getAccessoryInsuranceDocs(withToken, id),
      ]);

      const [checklist, insurance, ...rest] = fetchTasks;
      let utilities: PromiseSettledResult<MoveInUtilitiesStatus> | null = null;
      let rentSplit: PromiseSettledResult<MoveInRentSplitData> | null = null;
      let accDocs: PromiseSettledResult<AccessoryInsuranceDoc[]>;

      if (moveIn.showUtilitiesSection !== false && moveIn.showRentSplitSection !== false) {
        utilities = rest[0] as PromiseSettledResult<MoveInUtilitiesStatus>;
        rentSplit = rest[1] as PromiseSettledResult<MoveInRentSplitData>;
        accDocs = rest[2] as PromiseSettledResult<AccessoryInsuranceDoc[]>;
      } else if (moveIn.showUtilitiesSection !== false) {
        utilities = rest[0] as PromiseSettledResult<MoveInUtilitiesStatus>;
        accDocs = rest[1] as PromiseSettledResult<AccessoryInsuranceDoc[]>;
      } else if (moveIn.showRentSplitSection !== false) {
        rentSplit = rest[0] as PromiseSettledResult<MoveInRentSplitData>;
        accDocs = rest[1] as PromiseSettledResult<AccessoryInsuranceDoc[]>;
      } else {
        accDocs = rest[0] as PromiseSettledResult<AccessoryInsuranceDoc[]>;
      }

      if (checklist.status === 'fulfilled') setChecklistItems(checklist.value);
      setChecklistLoading(false);

      if (insurance.status === 'fulfilled') setInsuranceData(insurance.value);
      if (utilities && utilities.status === 'fulfilled') setUtilitiesData(utilities.value);
      if (accDocs.status === 'fulfilled') setAccessoryDocs(accDocs.value);

      if (rentSplit && rentSplit.status === 'fulfilled') {
        setRentSplitData(rentSplit.value);
        if (rentSplit.value.rentSplitData && Array.isArray(rentSplit.value.rentSplitData)) {
          const edits: Record<number, string> = {};
          for (const split of rentSplit.value.rentSplitData) {
            edits[split.tenantId] = String(split.amount);
          }
          setRentSplitEdits(edits);
        } else if (rentSplit.value.totalRent && rentSplit.value.tenants.length > 0) {
          const total = rentSplit.value.totalRent;
          const count = rentSplit.value.tenants.length;
          const base = Math.floor((total / count) * 100) / 100;
          const remainder = Math.round((total - base * count) * 100) / 100;

          const edits: Record<number, string> = {};
          const hm = rentSplit.value.tenants.find((t) => t.isHouseManager);
          for (const t of rentSplit.value.tenants) {
            const isHM = hm ? t.tenantId === hm.tenantId : false;
            edits[t.tenantId] = (isHM ? base + remainder : base).toFixed(2);
          }
          setRentSplitEdits(edits);
        }
      }
    })();
  }, [moveIn?.id, getToken]);

  useEffect(() => {
    if (!isByTheBed || !moveIn?.id) {
      setUnitRoommates(null);
      return;
    }
    let cancelled = false;
    setUnitRoommatesLoading(true);
    (async () => {
      try {
        const data = await getMoveInUnitRoommates(getToken, moveIn.id);
        if (!cancelled) setUnitRoommates(data);
      } catch {
        if (!cancelled) setUnitRoommates(null);
      } finally {
        if (!cancelled) setUnitRoommatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isByTheBed, moveIn?.id, getToken]);

  // --- Resolve current user's insurance record and house manager status ---
  const myInsurance = useMemo(() => {
    if (!insuranceData?.tenants) return null;
    const byFlag = insuranceData.tenants.find((t) => t.isCurrentUser);
    if (byFlag) return byFlag;
    if (insuranceData.currentUserTenantId) {
      const byServerId = insuranceData.tenants.find((t) => t.tenantId === insuranceData.currentUserTenantId);
      if (byServerId) return byServerId;
    }
    return insuranceData.tenants.find((t) => t.tenantId === tenant?.tenantId) ?? null;
  }, [insuranceData, tenant?.tenantId]);

  const isHouseManager = myInsurance?.isHouseManager ?? false;
  const canManageMoveIn = moveIn?.portalIsHouseManager ?? isHouseManager;
  const isJohnson390Property =
    moveIn?.propertyId === JOHNSON_390_PROPERTY_ID
    || (tenant?.residentialLease?.propertyName?.includes('390 Johnson') ?? false);
  const isJohnson390 = moveIn?.requiresCumEnrollmentForm === true || isJohnson390Property;
  /** 390 Johnson has default appointments in DB, but portal treats booking as pending until Aug 1. */
  const hideJohnson390AppointmentInPortal = isJohnson390 && !bookingOpen;
  const cumUtilityEnrollmentForm = useMemo(
    () => moveInContent.find((item) => item.sectionName === CUM_UTILITY_ENROLLMENT_SECTION),
    [moveInContent],
  );
  const leaseStartLabel = useMemo(() => {
    const raw = moveIn?.leaseStartDate?.slice(0, 10);
    if (!raw) return 'the first day of your lease contract';
    const [y, m, d] = raw.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [moveIn?.leaseStartDate]);
  const johnson390DefaultAppointment = hideJohnson390AppointmentInPortal
    ? {
        startTime: appointmentStartTime ?? moveIn?.scheduledDate ?? null,
        endTime: appointmentEndTime ?? null,
      }
    : null;
  const appointmentSectionComplete = mode === 'confirmed' && !hideJohnson390AppointmentInPortal;
  const houseManagerName = useMemo(() => {
    const hm = insuranceData?.tenants.find((t) => t.isHouseManager);
    return hm ? hm.firstName : null;
  }, [insuranceData?.tenants]);

  // --- Merged checklist (template-driven) ---
  const mergedTasks = useMemo(() =>
    MOVE_IN_CHECKLIST_TEMPLATE.map((t) => {
      const dbItem = checklistItems.find((ci) => ci.itemKey === t.itemKey);
      return {
        itemKey: t.itemKey,
        label: t.label,
        sortOrder: t.sortOrder,
        completed: dbItem?.completed ?? false,
      };
    }),
    [checklistItems]
  );

  const houseManagerOnlyTasks = new Set([
    UTILITIES_ENROLLMENT_FORM_ITEM_KEY,
    'utilities-confirmed',
    'rent-split-confirmed',
  ]);
  const requiresCumEnrollmentForm =
    moveIn?.requiresCumEnrollmentForm === true
    || utilitiesData?.requiresCumEnrollmentForm === true
    || (isJohnson390Property && showUtilitiesSection);
  const summaryTasks = useMemo<TaskSummaryItem[]>(
    () => {
      let tasks = mergedTasks.filter((t) => {
        if (t.itemKey === ROOMMATES_INTRODUCED_ITEM_KEY) return isByTheBed;
        if (t.itemKey === UTILITIES_ENROLLMENT_FORM_ITEM_KEY) {
          return showUtilitiesSection && requiresCumEnrollmentForm;
        }
        if (t.itemKey === 'utilities-confirmed') return showUtilitiesSection;
        if (t.itemKey === 'rent-split-confirmed') return showRentSplitSection;
        return true;
      });
      if (!canManageMoveIn) {
        tasks = tasks.filter((t) => !houseManagerOnlyTasks.has(t.itemKey));
      }
      if (hideJohnson390AppointmentInPortal) {
        tasks = tasks.map((t) =>
          t.itemKey === 'appointment-booked' ? { ...t, completed: false } : t
        );
      }
      return tasks;
    },
    [mergedTasks, canManageMoveIn, showUtilitiesSection, showRentSplitSection, hideJohnson390AppointmentInPortal, requiresCumEnrollmentForm, isByTheBed]
  );

  const totalTasks = summaryTasks.length;
  const completedTasks = summaryTasks.filter((t) => t.completed).length;
  const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // --- Computed task statuses ---
  const appointmentBooked = mergedTasks.find((t) => t.itemKey === 'appointment-booked')?.completed ?? false;
  const allInsuranceComplete = mergedTasks.find((t) => t.itemKey === 'all-insurance-complete')?.completed ?? false;
  const utilitiesConfirmed = mergedTasks.find((t) => t.itemKey === 'utilities-confirmed')?.completed ?? false;
  const enrollmentFormUploaded =
    mergedTasks.find((t) => t.itemKey === UTILITIES_ENROLLMENT_FORM_ITEM_KEY)?.completed ?? false;
  const utilitiesSectionComplete = requiresCumEnrollmentForm
    ? utilitiesConfirmed && enrollmentFormUploaded
    : utilitiesConfirmed;
  const rentSplitConfirmed = mergedTasks.find((t) => t.itemKey === 'rent-split-confirmed')?.completed ?? false;
  const roommatesIntroduced =
    mergedTasks.find((t) => t.itemKey === ROOMMATES_INTRODUCED_ITEM_KEY)?.completed ?? false;

  const insuranceEmailText = useMemo(() => {
    const name = [tenant?.firstName, tenant?.lastName].filter(Boolean).join(' ') || '[Your Name]';
    const address = tenant?.residentialLease?.address || '[Property Address]';
    const unit = tenant?.residentialLease?.unitNumber ? `Unit ${tenant.residentialLease.unitNumber}` : '';
    const fullAddress = [address, unit].filter(Boolean).join(', ');
    const leaseFrom = moveIn?.leaseStartDate?.slice(0, 10) ?? '[Lease Start Date]';
    const leaseTo = moveIn?.leaseEndDate?.slice(0, 10) ?? '[Lease End Date]';

    const tenantNames = insuranceData?.tenants.map((t) => `${t.firstName} ${t.lastName}`).join(', ') || name;

    return [
      `Hi,`,
      ``,
      `I am looking to set up a tenant insurance policy (not homeowner's insurance) for a rental unit. Below are the details and coverage requirements:`,
      ``,
      `Property Details:`,
      `- Type of coverage: Tenant/renter's insurance`,
      `- Property address: ${fullAddress}`,
      `- Lease term: ${leaseFrom} to ${leaseTo}`,
      `- Named insured(s): ${tenantNames}`,
      ``,
      `Required Coverage:`,
      `- Personal belongings/contents coverage`,
      `- Personal liability coverage of $1,000,000 or greater`,
      ``,
      `Recommended Additional Coverage:`,
      `- Additional living expenses in the case of displacement`,
      `- Sewer backup coverage`,
      `- Overland water coverage`,
      ``,
      `Please note:`,
      `- I am renting, not purchasing a property. I do not need coverage for the building structure itself.`,
      `- I do not need mortgage-related coverage, property/dwelling coverage, or homeowner's liability. These are covered by the property owner's insurance.`,
      `- I need a policy document or confirmation letter listing each named insured and the coverages included, which I can provide to my landlord.`,
      ``,
      `Could you please provide a quote and let me know what information you need from me to proceed?`,
      ``,
      `Thank you,`,
      `${name}`,
    ].join('\n');
  }, [tenant?.firstName, tenant?.lastName, tenant?.residentialLease, moveIn?.leaseStartDate, moveIn?.leaseEndDate, insuranceData?.tenants]);

  const handleCopyEmail = useCallback(() => {
    navigator.clipboard.writeText(insuranceEmailText).then(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2500);
    });
  }, [insuranceEmailText]);

  const utilitiesEmailTo = 'info@utilitieskingston.com';

  const utilitiesEmailSubject = useMemo(() => {
    const address = tenant?.residentialLease?.address || '[Property Address]';
    const unit = tenant?.residentialLease?.unitNumber ? `Unit ${tenant.residentialLease.unitNumber}` : '';
    const fullAddress = [address, unit].filter(Boolean).join(', ');
    return `Utilities Account Confirmation - ${fullAddress}`;
  }, [tenant?.residentialLease]);

  const utilitiesEmailText = useMemo(() => {
    const name = [tenant?.firstName, tenant?.lastName].filter(Boolean).join(' ') || '[Your Name]';
    const address = tenant?.residentialLease?.address || '[Property Address]';
    const unit = tenant?.residentialLease?.unitNumber ? `Unit ${tenant.residentialLease.unitNumber}` : '';
    const fullAddress = [address, unit].filter(Boolean).join(', ');
    return [
      `Hello,`,
      ``,
      `I am writing to confirm the setup of my utilities account at ${fullAddress}, effective May 1st, 2026.`,
      ``,
      `Could you please provide written confirmation that my account has been successfully activated?`,
      ``,
      `Thank you for your assistance.`,
      ``,
      `Best regards,`,
      `${name}`,
    ].join('\n');
  }, [tenant?.firstName, tenant?.lastName, tenant?.residentialLease]);

  const handleCopyUtilitiesEmail = useCallback(() => {
    navigator.clipboard.writeText(utilitiesEmailText).then(() => {
      setUtilitiesEmailCopied(true);
      setTimeout(() => setUtilitiesEmailCopied(false), 2500);
    });
  }, [utilitiesEmailText]);

  const handleUtilitiesUpload = useCallback(async (file: File) => {
    if (!moveIn?.id) return;
    setUtilitiesUploading(true);
    setUtilitiesUploadError(null);
    try {
      await uploadUtilitiesDocument(getToken, file, moveIn.id);

      setUtilitiesData((prev) => prev ? { ...prev, hasUtilitiesDocument: true } : prev);

      await toggleMoveInChecklistItem(getToken, moveIn.id, 'utilities-confirmed', true);
      const checklist = await getMoveInChecklist(getToken, moveIn.id);
      setChecklistItems(checklist);

      if (moveIn?.id) {
        const updated = await getMoveInUtilitiesStatus(getToken, moveIn.id);
        setUtilitiesData(updated);
      }
    } catch (err: any) {
      console.error('Utilities upload failed', err);
      setUtilitiesUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUtilitiesUploading(false);
      if (utilitiesFileRef.current) utilitiesFileRef.current.value = '';
    }
  }, [getToken, moveIn?.id]);

  const handleEnrollmentFormUpload = useCallback(async (file: File) => {
    if (!moveIn?.id) return;
    setEnrollmentUploading(true);
    setEnrollmentUploadError(null);
    try {
      await uploadUtilitiesEnrollmentForm(getToken, file, moveIn.id);

      setUtilitiesData((prev) => prev ? { ...prev, hasEnrollmentFormDocument: true } : prev);

      await toggleMoveInChecklistItem(getToken, moveIn.id, UTILITIES_ENROLLMENT_FORM_ITEM_KEY, true);
      const checklist = await getMoveInChecklist(getToken, moveIn.id);
      setChecklistItems(checklist);

      const updated = await getMoveInUtilitiesStatus(getToken, moveIn.id);
      setUtilitiesData(updated);
    } catch (err: any) {
      console.error('Enrollment form upload failed', err);
      setEnrollmentUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setEnrollmentUploading(false);
      if (enrollmentFileRef.current) enrollmentFileRef.current.value = '';
    }
  }, [getToken, moveIn?.id]);

  const handleEnrollmentFormDelete = useCallback(async () => {
    if (!moveIn?.id) return;
    setEnrollmentDeleting(true);
    setEnrollmentUploadError(null);
    try {
      await deleteUtilitiesEnrollmentForm(getToken, moveIn.id);
      setUtilitiesData((prev) =>
        prev
          ? {
              ...prev,
              hasEnrollmentFormDocument: false,
              enrollmentFormDocumentUrl: null,
              enrollmentFormDocumentFilename: null,
            }
          : prev
      );
      await toggleMoveInChecklistItem(getToken, moveIn.id, UTILITIES_ENROLLMENT_FORM_ITEM_KEY, false);
      const checklist = await getMoveInChecklist(getToken, moveIn.id);
      setChecklistItems(checklist);
      const updated = await getMoveInUtilitiesStatus(getToken, moveIn.id);
      setUtilitiesData(updated);
    } catch (err: any) {
      console.error('Enrollment form delete failed', err);
      setEnrollmentUploadError(err.message || 'Delete failed. Please try again.');
    } finally {
      setEnrollmentDeleting(false);
    }
  }, [getToken, moveIn?.id]);

  const handleUtilitiesDelete = useCallback(async () => {
    if (!moveIn?.id) return;
    setUtilitiesDeleting(true);
    setUtilitiesUploadError(null);
    try {
      await deleteUtilitiesDocument(getToken, moveIn.id);
      setUtilitiesData((prev) => prev ? { ...prev, hasUtilitiesDocument: false, documentUrl: null, documentFilename: null } : prev);
      await toggleMoveInChecklistItem(getToken, moveIn.id, 'utilities-confirmed', false);
      const checklist = await getMoveInChecklist(getToken, moveIn.id);
      setChecklistItems(checklist);
      if (moveIn?.id) {
        const updated = await getMoveInUtilitiesStatus(getToken, moveIn.id);
        setUtilitiesData(updated);
      }
    } catch (err: any) {
      console.error('Utilities delete failed', err);
      setUtilitiesUploadError(err.message || 'Delete failed. Please try again.');
    } finally {
      setUtilitiesDeleting(false);
    }
  }, [getToken, moveIn?.id]);

  const rentSplitTotal = rentSplitData?.tenants.reduce((sum, t) => sum + (parseFloat(rentSplitEdits[t.tenantId] || '0') || 0), 0) ?? 0;
  const rentSplitMatches = rentSplitData?.totalRent ? Math.abs(rentSplitTotal - rentSplitData.totalRent) < 0.01 : false;

  // --- Handlers ---
  const handleToggleTask = useCallback(async (itemKey: string, completed: boolean) => {
    if (!moveIn?.id) return;
    setChecklistItems((prev) => {
      const exists = prev.find((i) => i.itemKey === itemKey);
      if (exists) return prev.map((i) => i.itemKey === itemKey ? { ...i, completed } : i);
      const template = MOVE_IN_CHECKLIST_TEMPLATE.find((t) => t.itemKey === itemKey);
      return [...prev, {
        id: `temp-${itemKey}`,
        moveInId: moveIn.id,
        type: template?.type ?? 'GENERAL',
        itemKey,
        label: template?.label ?? itemKey,
        category: template?.category ?? null,
        completed,
        completedAt: completed ? new Date().toISOString() : null,
        completedByTenantId: tenant?.tenantId ?? null,
        metadata: null,
        sortOrder: template?.sortOrder ?? 99,
      }];
    });
    try {
      await toggleMoveInChecklistItem(getToken, moveIn.id, itemKey, completed);
    } catch (err) {
      console.error('Failed to toggle checklist item', err);
      setChecklistItems((prev) => prev.map((i) => i.itemKey === itemKey ? { ...i, completed: !completed } : i));
    }
  }, [moveIn?.id, getToken, tenant?.tenantId]);

  const hasAppointment = appointmentStartTime != null;

  const hasScheduledDate = moveIn?.scheduledDate != null;

  useEffect(() => {
    if (moveIn?.appointmentStartTime) {
      setOptimisticAppointment(null);
    }
  }, [moveIn?.appointmentStartTime]);

  useEffect(() => {
    if (hasAppointment) {
      setMode('confirmed');
    } else if (hasScheduledDate) {
      setMode('pending-confirm');
    } else {
      setMode('picker');
    }
  }, [hasAppointment, hasScheduledDate]);

  const anchorDate = useMemo(() => {
    const todayET = getEasternToday();
    if (appointmentStartTime) {
      const apptDate = new Date(appointmentStartTime).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      return apptDate >= todayET ? apptDate : todayET;
    }
    if (moveIn?.scheduledDate) {
      const schedDate = new Date(moveIn.scheduledDate).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      return schedDate >= todayET ? schedDate : todayET;
    }
    const leaseStart = moveIn?.leaseStartDate?.slice(0, 10) ?? todayET;
    return leaseStart >= todayET ? leaseStart : todayET;
  }, [moveIn?.scheduledDate, appointmentStartTime, moveIn?.leaseStartDate]);

  const dateWindow = useMemo(() => {
    if (isJohnson390 && moveIn?.scheduledDate) {
      const referenceDateET = appointmentStartTime
        ? new Date(appointmentStartTime).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
        : new Date(moveIn.scheduledDate).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      return buildMondaySundayWeek(referenceDateET, weekOffset);
    }
    return buildMoveInDateWindow(anchorDate, weekOffset, moveIn?.scheduledDate);
  }, [isJohnson390, moveIn?.scheduledDate, appointmentStartTime, anchorDate, weekOffset]);
  const canGoBack = weekOffset > 0;
  const canGoForward = dateWindow.length > 0;

  const blockedDates = useMemo(() => {
    const blocked = new Set<string>();

    if (isJohnson390) {
      for (const dateET of JOHNSON_390_MOVE_IN_BLOCKED_DATES_ET) {
        blocked.add(dateET);
      }
    }

    if (moveIn?.scheduledDate) {
      const schedET = new Date(moveIn.scheduledDate).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      if (schedET.endsWith('-05-01') || schedET.endsWith('-05-02')) {
        const year = parseInt(schedET.slice(0, 4), 10);
        blocked.add(`${year}-05-01`);
        blocked.add(`${year}-05-02`);
      }
    }

    return blocked.size > 0 ? blocked : undefined;
  }, [moveIn?.scheduledDate, isJohnson390]);

  const loadAllSlots = useCallback(async () => {
    if (!moveIn?.id || dateWindow.length === 0) return;
    setCalendarLoading(true);
    setBookingError(null);
    try {
      const todayET = getEasternToday();
      const futureDates = dateWindow.filter((d) => d >= todayET);
      if (futureDates.length === 0) { setSlotsByDate({}); return; }
      const result = await getMoveInSlotsForDates(getToken, moveIn.id, futureDates);
      setSlotsByDate(result);
    } catch (err) {
      setBookingError(err instanceof Error ? err.message : 'Failed to load availability');
      setSlotsByDate({});
    } finally {
      setCalendarLoading(false);
    }
  }, [moveIn?.id, dateWindow.join(','), getToken]);

  useEffect(() => {
    if (!bookingOpen || dateWindow.length === 0) return;
    const shouldLoadSlots =
      mode === 'picker' ||
      (isJohnson390 && mode === 'pending-confirm');
    if (shouldLoadSlots) {
      loadAllSlots();
    }
  }, [mode, bookingOpen, dateWindow.join(','), isJohnson390, loadAllSlots]);

  const handleBook = useCallback(async (slot: MoveInSlotInfo) => {
    if (!moveIn?.id) return;
    setBooking(true);
    setBookingError(null);
    try {
      await bookMoveInSlot(getToken, moveIn.id, slot.startTime);
      setOptimisticAppointment({
        startTime: slot.startTime,
        endTime: slot.endTime,
      });
      setMode('confirmed');
      setRescheduleFromStart(null);
      await refreshProfile();
      const checklist = await getMoveInChecklist(getToken, moveIn.id);
      setChecklistItems(checklist);
    } catch (err) {
      setOptimisticAppointment(null);
      setBookingError(err instanceof Error ? err.message : 'Booking failed');
      loadAllSlots();
    } finally {
      setBooking(false);
    }
  }, [moveIn?.id, getToken, refreshProfile, loadAllSlots]);

  const handleReschedule = useCallback(() => {
    setRescheduleFromStart(appointmentStartTime ?? moveIn?.scheduledDate ?? null);
    setWeekOffset(0);
    setMode('picker');
    setSlotsByDate({});
  }, [appointmentStartTime, moveIn?.scheduledDate]);

  const handleConfirmScheduledDate = useCallback(async () => {
    if (!moveIn?.id) return;
    setConfirming(true);
    setBookingError(null);
    try {
      await confirmMoveInScheduledDate(getToken, moveIn.id);
      await refreshProfile();
      const checklist = await getMoveInChecklist(getToken, moveIn.id);
      setChecklistItems(checklist);
      setMode('confirmed');
    } catch (err) {
      setBookingError(err instanceof Error ? err.message : 'Failed to confirm scheduled date');
    } finally {
      setConfirming(false);
    }
  }, [moveIn?.id, getToken, refreshProfile]);

  const handleBookDifferentTime = useCallback(() => {
    setRescheduleFromStart(moveIn?.scheduledDate ?? null);
    setWeekOffset(0);
    setMode('picker');
    setSlotsByDate({});
  }, [moveIn?.scheduledDate]);

  const handleSaveRentSplit = useCallback(async () => {
    if (!moveIn?.id || !rentSplitData) return;
    setRentSplitSaving(true);
    try {
      const splits = rentSplitData.tenants.map((t) => ({
        tenantId: t.tenantId,
        amount: parseFloat(rentSplitEdits[t.tenantId] || '0') || 0,
      }));
      await saveMoveInRentSplit(getToken, moveIn.id, splits);
      if (rentSplitData.totalRent && Math.abs(splits.reduce((s, x) => s + x.amount, 0) - rentSplitData.totalRent) < 0.01) {
        await toggleMoveInChecklistItem(getToken, moveIn.id, 'rent-split-confirmed', true);
        const checklist = await getMoveInChecklist(getToken, moveIn.id);
        setChecklistItems(checklist);
      }
      setRentSplitData((prev) => prev ? { ...prev, rentSplitData: splits } : prev);
    } catch (err) {
      console.error('Failed to save rent split', err);
    } finally {
      setRentSplitSaving(false);
    }
  }, [moveIn?.id, getToken, rentSplitData, rentSplitEdits]);

  const handleInsuranceUpload = useCallback(async (file: File) => {
    if (!moveIn?.id) return;
    setInsuranceUploading(true);
    setInsuranceUploadError(null);
    try {
      const result = await uploadInsuranceDocument(getToken, file, moveIn.id);

      // Optimistic update: mark the current tenant's insurance as submitted
      const uploadedId = result.tenantId;
      setInsuranceData((prev) => {
        if (!prev) return prev;
        const isMatch = (t: MoveInTenantInsurance) =>
          t.isCurrentUser || t.tenantId === uploadedId || t.tenantId === prev.currentUserTenantId;
        const updatedTenants = prev.tenants.map((t) =>
          isMatch(t) ? { ...t, hasInsuranceDocument: true, insuranceConfirmed: false } : t
        );
        return { ...prev, tenants: updatedTenants, allComplete: updatedTenants.every((t) => t.hasInsuranceDocument) };
      });

      // Refresh from server to check if all tenants have now uploaded
      if (moveIn?.id) {
        const updated = await getMoveInTenantsInsurance(getToken, moveIn.id);
        setInsuranceData(updated);
        if (updated.tenants.every((t) => t.hasInsuranceDocument)) {
          await toggleMoveInChecklistItem(getToken, moveIn.id, 'all-insurance-complete', true);
          const checklist = await getMoveInChecklist(getToken, moveIn.id);
          setChecklistItems(checklist);
        }
      }
    } catch (err: any) {
      console.error('Insurance upload failed', err);
      setInsuranceUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setInsuranceUploading(false);
      if (insuranceFileRef.current) insuranceFileRef.current.value = '';
    }
  }, [getToken, moveIn?.id, tenant?.tenantId]);

  const [insuranceDeleting, setInsuranceDeleting] = useState(false);
  const handleInsuranceDelete = useCallback(async () => {
    if (!moveIn?.id) return;
    setInsuranceDeleting(true);
    setInsuranceUploadError(null);
    try {
      await deleteInsuranceDocument(getToken, moveIn.id);
      setInsuranceData((prev) => {
        if (!prev) return prev;
        const isMatch = (t: MoveInTenantInsurance) =>
          t.isCurrentUser || t.tenantId === prev.currentUserTenantId;
        const updatedTenants = prev.tenants.map((t) =>
          isMatch(t) ? { ...t, hasInsuranceDocument: false, insuranceConfirmed: false, documentUrl: null, documentFilename: null } : t
        );
        return { ...prev, tenants: updatedTenants, allComplete: false };
      });
      await toggleMoveInChecklistItem(getToken, moveIn.id, 'all-insurance-complete', false);
      const checklist = await getMoveInChecklist(getToken, moveIn.id);
      setChecklistItems(checklist);
      if (moveIn?.id) {
        const updated = await getMoveInTenantsInsurance(getToken, moveIn.id);
        setInsuranceData(updated);
      }
    } catch (err: any) {
      console.error('Insurance delete failed', err);
      setInsuranceUploadError(err.message || 'Delete failed. Please try again.');
    } finally {
      setInsuranceDeleting(false);
    }
  }, [getToken, moveIn?.id]);

  const refreshAccessoryDocs = useCallback(async () => {
    if (!moveIn?.id) return;
    try {
      const docs = await getAccessoryInsuranceDocs(getToken, moveIn.id);
      setAccessoryDocs(docs);
    } catch { /* ignore */ }
  }, [getToken, moveIn?.id]);

  const handleAccessoryUpload = useCallback(async (file: File) => {
    if (!moveIn?.id) return;
    if (accessoryDocs.length >= 5) {
      setAccessoryUploadError('Maximum of 5 additional documents reached.');
      return;
    }
    setAccessoryUploading(true);
    setAccessoryUploadError(null);
    try {
      const label = file.name.replace(/\.[^.]+$/, '') || 'Insurance Document';
      await uploadAccessoryInsuranceDoc(getToken, file, moveIn.id, label);
      await refreshAccessoryDocs();
    } catch (err: any) {
      console.error('Accessory insurance upload failed', err);
      setAccessoryUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setAccessoryUploading(false);
      if (accessoryInsFileRef.current) accessoryInsFileRef.current.value = '';
    }
  }, [getToken, moveIn?.id, accessoryDocs.length, refreshAccessoryDocs]);

  const handleAccessoryDelete = useCallback(async (documentId: string) => {
    if (!moveIn?.id) return;
    setAccessoryDeleting(documentId);
    try {
      await deleteAccessoryInsuranceDoc(getToken, moveIn.id, documentId);
      await refreshAccessoryDocs();
    } catch (err: any) {
      console.error('Accessory insurance delete failed', err);
      setAccessoryUploadError(err.message || 'Delete failed. Please try again.');
    } finally {
      setAccessoryDeleting(null);
    }
  }, [getToken, moveIn?.id, refreshAccessoryDocs]);

  // --- Section visibility tracking ---
  const houseManagerOnlySections = new Set(['utilities', 'rent-split']);
  const visibleSections = useMemo(() => {
    let filtered = PAGE_SECTIONS.filter((s) => {
      if (s.id === 'unit-roommates') return isByTheBed;
      if (s.id === 'utilities') return showUtilitiesSection && canManageMoveIn;
      if (s.id === 'rent-split') return showRentSplitSection && canManageMoveIn;
      return !houseManagerOnlySections.has(s.id) || canManageMoveIn;
    });
    let stepCounter = 0;
    return filtered.map((s) => s.step ? { ...s, step: ++stepCounter } : s);
  }, [canManageMoveIn, showUtilitiesSection, showRentSplitSection, isByTheBed]);

  const sectionStepById = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of visibleSections) {
      if (s.step != null) map[s.id] = s.step;
    }
    return map;
  }, [visibleSections]);

  const [activeSection, setActiveSection] = useState<string | null>(PAGE_SECTIONS[0]?.id ?? null);

  useEffect(() => {
    const sectionEls = visibleSections.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
    if (sectionEls.length === 0) return;

    const scrollRoot = document.querySelector('main');

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { root: scrollRoot, rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // --- Page-level loading gate ---
  const pageReady = !profileLoading && (!moveIn?.id || !checklistLoading);

  if (!pageReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="relative">
          <div className="w-14 h-14 rounded-full border-4 border-gray-200 dark:border-white/10" />
          <div className="absolute inset-0 w-14 h-14 rounded-full border-4 border-red-700 border-t-transparent animate-spin" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Preparing your move-in portal</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Loading your information…</p>
        </div>
      </div>
    );
  }

  if (!moveIn) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-sm text-gray-400 dark:text-gray-500">No upcoming move-in found.</p>
      </div>
    );
  }

  return (
    <>
      <SectionTracker sections={visibleSections} activeId={activeSection} />

      <div className="w-full sm:w-[94%] md:w-[92%] xl:w-[88%] 2xl:w-[76%] max-w-5xl mx-auto space-y-6">
        <div data-sticky-header className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm rounded-b-2xl shadow-md border border-gray-200 dark:border-white/10 px-5 py-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <PackageCheck size={28} className="text-red-700 dark:text-red-400" />
            Move In
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Everything you need to know about your upcoming move-in.
          </p>

          {totalTasks > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {completedTasks} of {totalTasks} tasks complete
                </span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => scrollToSection('task-summary')}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 rounded-full px-3 py-1 hover:bg-red-100 hover:border-red-300 transition-all shadow-sm"
                  >
                    <ListChecks size={12} />
                    Return to Summary
                  </button>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{progressPct}%</span>
                </div>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-red-700 transition-all duration-500 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Section: Welcome */}
        <section id="welcome">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-white/10 overflow-hidden">
            <div className="px-5 py-6 space-y-3">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">It's Almost Time to Move In!</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                We're excited to have you join us. The following steps must be completed before being provided access to your unit.
              </p>
              {moveIn.leaseStartDate && (() => {
                const [y, m, d] = moveIn.leaseStartDate.split('-').map(Number);
                const dt = new Date(y, m - 1, d);
                const label = dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                return (
                  <div className="flex justify-center">
                    <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-950/40 text-red-800 rounded-lg px-4 py-2.5">
                      <CalendarDays size={16} className="shrink-0" />
                      <span className="text-sm font-medium">Your lease begins on {label}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Task summary */}
        <div id="task-summary">
          {!checklistLoading && <TaskSummary items={summaryTasks} onNavigate={(sectionId) => {
            setForceOpenId((prev) => ({ sectionId, rev: prev.rev + 1 }));
            setTimeout(() => scrollToSection(sectionId), 50);
          }} />}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={expandAll} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 shadow-sm hover:bg-gray-50 dark:hover:bg-white/5 hover:border-gray-300 transition-all">
            <ChevronsDown size={13} />
            Expand All Sections
          </button>
          <button type="button" onClick={collapseAll} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 shadow-sm hover:bg-gray-50 dark:hover:bg-white/5 hover:border-gray-300 transition-all">
            <ChevronsUp size={13} />
            Collapse All Sections
          </button>
        </div>

        {/* Section: Book Your Move-In Appointment */}
        <section id="book-appointment">
          <CollapsibleSection number={sectionStepById['book-appointment'] ?? 1} title={canManageMoveIn ? 'Book Your Move-In Appointment' : 'Move-In Appointment'} badge={canManageMoveIn && showHouseManagerLabel ? 'House Manager' : undefined} isComplete={appointmentSectionComplete} forceToggle={sectionToggle} sectionId="book-appointment" forceOpenId={forceOpenId}>

          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Move-In FAQs</h3>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 px-5 py-2">
            <FaqAccordion items={MOVE_IN_FAQ} />
          </div>

          <div className={`rounded-xl shadow-sm border p-6 space-y-5 ${
            mode === 'pending-confirm' && !isJohnson390
              ? 'bg-red-50 border-red-200'
              : 'bg-white border-gray-200'
          }`}>
            {canManageMoveIn ? (
              <>
                {!bookingOpen && (hideJohnson390AppointmentInPortal || mode === 'picker' || mode === 'pending-confirm') && (
                  <MoveInBookingNotYetOpenPanel
                    countdown={countdown}
                    johnson390Default={
                      johnson390DefaultAppointment?.startTime
                        ? {
                            startTime: johnson390DefaultAppointment.startTime,
                            endTime: johnson390DefaultAppointment.endTime,
                          }
                        : null
                    }
                  />
                )}

                {bookingOpen && isJohnson390 && moveIn && (
                  <Johnson390MoveInAppointmentBooking
                    mode={mode}
                    bookingError={bookingError}
                    scheduledDate={moveIn.scheduledDate}
                    appointmentStartTime={appointmentStartTime}
                    appointmentEndTime={appointmentEndTime}
                    isByTheBed={isByTheBed}
                    lease={tenant?.residentialLease}
                    confirming={confirming}
                    booking={booking}
                    calendarLoading={calendarLoading}
                    dateWindow={dateWindow}
                    slotsByDate={slotsByDate}
                    rescheduleFromStart={rescheduleFromStart}
                    canGoBack={canGoBack}
                    canGoForward={canGoForward}
                    blockedDates={blockedDates}
                    todayET={getEasternToday()}
                    rescheduleAvailable={bookingOpen}
                    onReschedule={handleReschedule}
                    onConfirm={handleConfirmScheduledDate}
                    onBookDifferent={handleBookDifferentTime}
                    onBook={handleBook}
                    onPrevWeek={() => setWeekOffset((o) => Math.max(o - 1, 0))}
                    onNextWeek={() => setWeekOffset((o) => o + 1)}
                  />
                )}

                {bookingOpen && !isJohnson390 && moveIn && (
                  <MoveInAppointmentBookingFlow
                    mode={mode}
                    bookingError={bookingError}
                    scheduledDate={moveIn.scheduledDate}
                    appointmentStartTime={appointmentStartTime}
                    appointmentEndTime={appointmentEndTime}
                    lease={tenant?.residentialLease}
                    confirming={confirming}
                    booking={booking}
                    calendarLoading={calendarLoading}
                    dateWindow={dateWindow}
                    slotsByDate={slotsByDate}
                    rescheduleFromStart={rescheduleFromStart}
                    canGoBack={canGoBack}
                    canGoForward={canGoForward}
                    blockedDates={blockedDates}
                    todayET={getEasternToday()}
                    rescheduleAvailable={bookingOpen}
                    onReschedule={handleReschedule}
                    onConfirm={handleConfirmScheduledDate}
                    onBookDifferent={handleBookDifferentTime}
                    onBook={handleBook}
                    onPrevWeek={() => setWeekOffset((o) => Math.max(o - 1, 0))}
                    onNextWeek={() => setWeekOffset((o) => o + 1)}
                  />
                )}
              </>
            ) : (
              <>
                {hideJohnson390AppointmentInPortal ? (
                  <MoveInBookingNotYetOpenPanel
                    countdown={countdown}
                    johnson390Default={
                      johnson390DefaultAppointment?.startTime
                        ? {
                            startTime: johnson390DefaultAppointment.startTime,
                            endTime: johnson390DefaultAppointment.endTime,
                          }
                        : null
                    }
                  />
                ) : appointmentStartTime && appointmentEndTime ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-xl overflow-hidden">
                      <div className="flex items-center gap-2.5 px-5 py-3 border-b border-green-200 dark:border-green-900/40">
                        <CheckCircle2 size={18} className="text-green-600 shrink-0" />
                        <h3 className="text-sm font-semibold text-green-900">Your move-in is scheduled</h3>
                      </div>
                      <div className="divide-y divide-green-100">
                        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
                          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Date</span>
                          <span className="text-sm text-green-900">{formatConfirmedDateISO(appointmentStartTime)}</span>
                        </div>
                        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
                          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Day</span>
                          <span className="text-sm text-green-900">{formatConfirmedDayOfWeek(appointmentStartTime)}</span>
                        </div>
                        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
                          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Time</span>
                          <span className="text-sm text-green-900">{formatConfirmedTime(appointmentStartTime, appointmentEndTime)}</span>
                        </div>
                      </div>
                      <div className="px-5 py-3 border-t border-green-200 dark:border-green-900/40">
                        <button
                          onClick={() => downloadMoveInICS(appointmentStartTime, appointmentEndTime, tenant?.residentialLease)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 bg-white dark:bg-gray-800 text-green-700 dark:text-green-400 hover:bg-green-100 transition-colors"
                        >
                          <CalendarPlus size={14} />
                          Add to Calendar
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <AlertCircle size={16} className="text-blue-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-blue-800">
                        Booking and rescheduling the move-in appointment is managed by the house manager.
                        {houseManagerName && <> Please contact <strong>{houseManagerName}</strong> if you would like to request a change.</>}
                      </p>
                    </div>
                  </div>
                ) : moveIn.scheduledDate ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2.5">
                      <CalendarDays size={18} className="text-gray-500 dark:text-gray-400 shrink-0" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Your move-in appointment is scheduled for:</h3>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatFullDate(moveIn.scheduledDate)} at {formatSlotTime(moveIn.scheduledDate)} ET
                    </p>
                    <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-lg">
                      <Clock size={16} className="text-amber-600 shrink-0" />
                      <p className="text-sm text-amber-800 dark:text-amber-300">
                        <span className="font-medium">Pending confirmation</span> — your house manager
                        {houseManagerName && <> (<strong>{houseManagerName}</strong>)</>} will confirm this appointment.
                      </p>
                    </div>
                  </div>
                ) : !bookingOpen ? (
                  <MoveInBookingNotYetOpenPanel countdown={countdown} />
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2.5 p-4 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl">
                      <Clock size={18} className="text-gray-400 dark:text-gray-500 shrink-0" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">No move-in appointment has been scheduled yet.</p>
                    </div>
                    <div className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <AlertCircle size={16} className="text-blue-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-blue-800">
                        Booking the move-in appointment is managed by the house manager.
                        {houseManagerName && <> Please contact <strong>{houseManagerName}</strong> if you have any questions.</>}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          </CollapsibleSection>
        </section>

        {/* Section: Tenant Insurance */}
        <section id="insurance">
          <CollapsibleSection number={sectionStepById['insurance'] ?? 2} title="Tenant Insurance" isComplete={allInsuranceComplete} forceToggle={sectionToggle} sectionId="insurance" forceOpenId={forceOpenId}>

          <div className="space-y-5">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Every tenant on the lease must have their own tenant insurance policy.
              Below you can see the status of each tenant's insurance upload.
            </p>

            {/* Upload status table */}
            <div className="border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
                    <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-2.5">Tenant</th>
                    <th className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-3 py-2.5 w-28">Submitted</th>
                    <th className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-3 py-2.5 w-32">Under Review</th>
                    <th className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-3 py-2.5 w-28">Approved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {insuranceData?.tenants.map((t) => {
                    const submitted = t.hasInsuranceDocument;
                    const accepted = t.insuranceConfirmed;
                    const underReview = submitted && !accepted;

                    return (
                      <tr key={t.tenantId} className={accepted ? 'bg-green-50/50' : ''}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {t.isHouseManager && showHouseManagerLabel && (
                              <Star size={14} className="text-amber-500 fill-amber-500 shrink-0" />
                            )}
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{t.firstName} {t.lastName}</span>
                            {t.isHouseManager && showHouseManagerLabel && (
                              <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-full px-1.5 py-0.5">
                                House Manager
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {submitted ? (
                            <CheckCircle2 size={18} className="text-green-600 mx-auto" />
                          ) : (
                            <Circle size={18} className="text-gray-300 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {underReview ? (
                            <Clock size={18} className="text-amber-500 mx-auto" />
                          ) : submitted ? (
                            <CheckCircle2 size={18} className="text-green-600 mx-auto" />
                          ) : (
                            <Circle size={18} className="text-gray-300 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {accepted ? (
                            <CheckCircle2 size={18} className="text-green-600 mx-auto" />
                          ) : (
                            <Circle size={18} className="text-gray-300 mx-auto" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Confirm all insurance uploaded */}
            {(() => {
              const allUploaded = insuranceData?.tenants.every((t) => t.hasInsuranceDocument) ?? false;
              const locked = !allUploaded;
              const uploadedCount = insuranceData?.tenants.filter((t) => t.hasInsuranceDocument).length ?? 0;
              const totalCount = insuranceData?.tenants.length ?? 0;

              return (
                <div className={`rounded-xl p-5 space-y-4 transition-colors ${
                  allInsuranceComplete
                    ? 'bg-green-50 border border-green-200'
                    : locked
                      ? 'bg-gray-50 border border-gray-200'
                      : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      allInsuranceComplete ? 'bg-green-600' : locked ? 'bg-gray-300' : 'bg-red-600'
                    }`}>
                      {allInsuranceComplete
                        ? <Check size={16} className="text-white" />
                        : <PackageCheck size={14} className="text-white" />
                      }
                    </div>
                    <div>
                      <p className={`text-[10px] font-bold uppercase tracking-wider ${
                        allInsuranceComplete ? 'text-green-600' : locked ? 'text-gray-400' : 'text-red-500'
                      }`}>Confirm</p>
                      <h4 className={`text-sm font-bold uppercase tracking-wide ${
                        allInsuranceComplete ? 'text-green-900' : locked ? 'text-gray-500' : 'text-red-900'
                      }`}>All Insurance Policies Uploaded</h4>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    {allInsuranceComplete
                      ? 'All tenants have submitted their insurance policies. Amber Peak will review each document.'
                      : locked
                        ? `All tenants on the lease must upload their insurance policy before this can be confirmed (${uploadedCount}/${totalCount} uploaded).`
                        : 'All tenants have uploaded their insurance policy. Please confirm to proceed.'}
                  </p>

                  <div
                    role="checkbox"
                    aria-checked={allInsuranceComplete}
                    aria-disabled
                    tabIndex={-1}
                    className="flex items-center gap-3 opacity-50 cursor-not-allowed"
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      allInsuranceComplete
                        ? 'bg-green-600 border-green-600 text-white'
                        : locked
                          ? 'border-gray-300'
                          : 'border-red-400 group-hover:border-red-500'
                    }`}>
                      {allInsuranceComplete && <Check size={13} strokeWidth={3} />}
                    </div>
                    <span className={`text-sm leading-snug ${allInsuranceComplete ? 'text-green-800 line-through' : locked ? 'text-gray-400' : 'text-gray-800'}`}>
                      All tenant insurance uploads are complete
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Upload your insurance */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Upload Your Insurance Policy</h4>
              </div>
              <div className="px-5 py-5 space-y-4">
                {myInsurance?.hasInsuranceDocument ? (
                  <div className="space-y-3">
                    {/* Uploaded document row */}
                    <div className={`flex items-center gap-2 rounded-md px-3 py-2.5 ${
                      myInsurance.insuranceConfirmed
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-amber-50 border border-amber-200'
                    }`}>
                      <Paperclip size={14} className={myInsurance.insuranceConfirmed ? 'text-green-600 shrink-0' : 'text-amber-600 shrink-0'} />
                      <span className={`text-sm font-medium truncate flex-1 ${myInsurance.insuranceConfirmed ? 'text-green-900' : 'text-amber-900'}`}>
                        {myInsurance.documentFilename || 'Insurance policy'}
                      </span>
                      <span className={`text-[10px] font-medium shrink-0 ${myInsurance.insuranceConfirmed ? 'text-green-600' : 'text-amber-600'}`}>
                        {myInsurance.insuranceConfirmed ? 'Approved' : 'Pending review'}
                      </span>
                      {myInsurance.documentUrl && (
                        <>
                          <span className={myInsurance.insuranceConfirmed ? 'text-green-300 shrink-0' : 'text-amber-300 shrink-0'}>|</span>
                          <a
                            href={myInsurance.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-xs font-semibold shrink-0 ${myInsurance.insuranceConfirmed ? 'text-green-700 hover:text-green-900' : 'text-amber-700 hover:text-amber-900'}`}
                          >
                            View
                          </a>
                        </>
                      )}
                      <span className={myInsurance.insuranceConfirmed ? 'text-green-300 shrink-0' : 'text-amber-300 shrink-0'}>|</span>
                      <button
                        type="button"
                        onClick={() => insuranceFileRef.current?.click()}
                        disabled={insuranceUploading || insuranceDeleting}
                        className={`text-xs font-semibold shrink-0 ${myInsurance.insuranceConfirmed ? 'text-green-700 hover:text-green-900' : 'text-amber-700 hover:text-amber-900'}`}
                      >
                        {insuranceUploading ? <Loader2 size={14} className="animate-spin" /> : 'Replace'}
                      </button>
                      <span className={myInsurance.insuranceConfirmed ? 'text-green-300 shrink-0' : 'text-amber-300 shrink-0'}>|</span>
                      <button
                        type="button"
                        onClick={handleInsuranceDelete}
                        disabled={insuranceUploading || insuranceDeleting}
                        className="text-xs font-semibold shrink-0 text-red-600 dark:text-red-400 hover:text-red-800"
                      >
                        {insuranceDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>

                    {!myInsurance.insuranceConfirmed && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Amber Peak will review your document shortly. You will be notified once it has been approved.
                      </p>
                    )}
                  </div>
                ) : (
                  <div
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setInsuranceDragOver(true); }}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setInsuranceDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setInsuranceDragOver(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setInsuranceDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) handleInsuranceUpload(file);
                    }}
                    onClick={() => { if (!insuranceUploading) insuranceFileRef.current?.click(); }}
                    className={`relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer ${
                      insuranceUploading
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                        : insuranceDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/30'
                    }`}
                  >
                    {insuranceUploading ? (
                      <>
                        <Loader2 size={28} className="text-blue-500 animate-spin" />
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Uploading your document...</p>
                      </>
                    ) : (
                      <>
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Upload size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                            Drag and drop your insurance policy here, or <span className="text-blue-600 underline underline-offset-2">browse files</span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">PDF, JPEG, or PNG. Max 10 MB.</p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {insuranceUploadError && (
                  <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 px-3 py-2">
                    <AlertCircle size={16} className="text-red-500 shrink-0" />
                    <p className="text-xs text-red-700 dark:text-red-400">{insuranceUploadError}</p>
                  </div>
                )}

                <input
                  ref={insuranceFileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleInsuranceUpload(file);
                  }}
                />
              </div>
            </div>

            {/* Optional extra insurance files — distinct from primary (blue) upload; uses app red + neutral grays */}
            {myInsurance?.hasInsuranceDocument && (
            <div className="mt-2 rounded-2xl border border-red-200 dark:border-red-900/40 bg-gradient-to-b from-red-50/70 to-white shadow-sm overflow-hidden ring-1 ring-red-100">
              <div className="px-5 py-4 border-b border-red-100 dark:border-red-900/40 bg-red-50/50">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-200 border border-red-200 dark:border-red-900/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                    Optional
                  </span>
                  <Layers size={16} className="text-red-700 dark:text-red-400 shrink-0" aria-hidden />
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">Extra insurance documents</h4>
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-100 leading-relaxed">
                  Do you have additional documents related to your insurance that you would like to share with us? Please upload them here.
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 leading-relaxed">
                  Use this only for <span className="font-semibold text-gray-800 dark:text-gray-100">extra</span> items—such as a broker letter, policy addendum, or declaration page—not a second copy of your main policy.
                </p>
              </div>
              <div className="px-5 py-5 space-y-3">
                {/* Existing uploaded accessory docs */}
                {accessoryLoading ? (
                  <div className="flex items-center gap-2 py-4 justify-center">
                    <Loader2 size={16} className="text-red-600 dark:text-red-400 animate-spin" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Loading documents...</span>
                  </div>
                ) : accessoryDocs.length > 0 && (
                  <div className="space-y-2">
                    {accessoryDocs.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-2 rounded-lg px-3 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                        <Paperclip size={14} className="text-red-700 dark:text-red-400 shrink-0" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                          {doc.name}
                        </span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 shrink-0">
                          {new Date(doc.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        {doc.downloadUrl && (
                          <>
                            <span className="text-gray-300 shrink-0">|</span>
                            <a
                              href={doc.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-semibold text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200 shrink-0"
                            >
                              View
                            </a>
                          </>
                        )}
                        <span className="text-gray-300 shrink-0">|</span>
                        <button
                          type="button"
                          onClick={() => handleAccessoryDelete(doc.id)}
                          disabled={accessoryDeleting === doc.id}
                          className="text-xs font-semibold shrink-0 text-red-600 dark:text-red-400 hover:text-red-800"
                        >
                          {accessoryDeleting === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload zone — list-style add, not the same dashed “hero” as primary policy */}
                {accessoryDocs.length < 5 ? (
                  <div
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setAccessoryDragOver(true); }}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setAccessoryDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setAccessoryDragOver(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAccessoryDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) handleAccessoryUpload(file);
                    }}
                    onClick={() => { if (!accessoryUploading) accessoryInsFileRef.current?.click(); }}
                    className={`relative flex flex-row items-center gap-4 rounded-lg border px-4 py-4 text-left transition-colors cursor-pointer ${
                      accessoryUploading
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                        : accessoryDragOver
                          ? 'border-red-400 bg-red-50'
                          : 'border-red-200 border-dashed bg-white hover:bg-red-50/50 hover:border-red-400'
                    }`}
                  >
                    {accessoryUploading ? (
                      <>
                        <Loader2 size={22} className="text-red-700 dark:text-red-400 animate-spin shrink-0" />
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Uploading…</p>
                      </>
                    ) : (
                      <>
                        <div className="w-11 h-11 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center shrink-0 border border-red-200 dark:border-red-900/40">
                          <Plus size={22} className="text-red-800" strokeWidth={2.25} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            Add an optional file
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            Drag and drop here, or <span className="font-medium text-red-700 dark:text-red-400 underline decoration-red-300">choose a file</span> — PDF, JPEG, or PNG, max 10 MB. Up to {5 - accessoryDocs.length} more.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-600 dark:text-gray-400 text-center py-2">
                    Maximum of 5 additional documents reached.
                  </p>
                )}

                {accessoryUploadError && (
                  <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 px-3 py-2">
                    <AlertCircle size={16} className="text-red-500 shrink-0" />
                    <p className="text-xs text-red-700 dark:text-red-400">{accessoryUploadError}</p>
                  </div>
                )}

                <input
                  ref={accessoryInsFileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAccessoryUpload(file);
                  }}
                />
              </div>
            </div>
            )}

            {/* Why Tenant Insurance is Needed + Coverage Requirements */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Why Tenant Insurance is Needed?</h4>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                {/* Left: Explanation */}
                <div className="px-5 py-4 space-y-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    Amber Peak's insurance does not provide coverage for your personal losses while living in your unit.
                  </p>
                  <p>
                    Amber Peak maintains insurance on the physical structure of the building. If damage were to occur to the building in a heavy storm, Amber Peak would make a claim to their insurance provider to obtain financial resources to make repairs.
                  </p>
                  <p>
                    Amber Peak's insurance coverage would not cover your possessions in this scenario. If your furniture, clothing, phones, laptops, tablets, or other personal items were damaged, these items would have zero coverage. If you were forced to temporarily move out of the property due to damages, additional living expenses would not be covered.
                  </p>
                  <p>
                    While living in your unit, there is always a risk that your personal belongings could be damaged or stolen, or that the unit could become temporarily uninhabitable. Fire, floods, break-ins, and sewer backups are just some examples of potential risks that could occur while occupying the unit.
                  </p>
                  <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-2.5">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold text-gray-900 dark:text-white">Tenant insurance will provide you protection against loss in these situations.</span> Tenant insurance covers:
                    </p>
                    <ul className="space-y-2 pl-1">
                      <li className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span> Loss or damage to personal belongings
                      </li>
                      <li className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span> Temporary living expenses if you must move out due to a physical issue with your unit
                      </li>
                      <li className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span>
                        <div>
                          Personal liability coverage, which may protect you if:
                          <ul className="mt-1.5 space-y-1.5 pl-1">
                            <li className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                              <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span> A guest is injured in your unit
                            </li>
                            <li className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                              <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span> You accidentally cause damage to the building or another tenant's property (e.g. you flood a neighbour's unit)
                            </li>
                          </ul>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <p>
                    Amber Peak cannot and does not guarantee that your stay will be free from incidents. Certain risks are inherent to living in any residential property.
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    As a condition of your lease, all tenants are required to be covered by a tenant insurance policy for the duration of their tenancy.
                  </p>
                </div>

                {/* Right: Coverage Requirements Table */}
                <div className="px-5 py-4 flex items-center justify-center">
                  <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-5 space-y-4 w-full">
                    <div className="text-center space-y-1">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Coverage Requirements</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">As per Amber Peak's lease appendix on your lease agreement</p>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 uppercase tracking-wide mb-2">Required</p>
                      <ul className="space-y-1.5">
                        <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="text-green-500 mt-0.5">•</span> Personal belongings/contents coverage
                        </li>
                        <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="text-green-500 mt-0.5">•</span> Personal liability coverage of $1,000,000 or greater
                        </li>
                      </ul>
                    </div>

                    <div className="border-t border-gray-100 dark:border-white/5 pt-3">
                      <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 uppercase tracking-wide mb-2">Optional</p>
                      <ul className="space-y-1.5">
                        <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span> Additional living expenses in the case of displacement
                        </li>
                        <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span> Identity theft coverage
                        </li>
                        <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span> Displacement due to overland water
                        </li>
                        <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="text-gray-400 dark:text-gray-500 mt-0.5">•</span> Displacement due to sewer backup
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Securing your tenant insurance */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Options for Securing Your Tenant Insurance</h4>
              </div>
              <div className="px-5 py-5 space-y-5">
                {/* Flow diagram — SVG edges with foreignObject nodes */}
                <div className="w-full overflow-hidden">
                  <svg viewBox="0 0 600 180" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#cbd5e1" />
                      </marker>
                    </defs>
                    {/* Edges: smooth bezier curves from parent bottom-center to each child top-center */}
                    <path d="M 300,52 C 300,85 160,85 160,118" fill="none" stroke="#cbd5e1" strokeWidth="1.5" markerEnd="url(#arrow)" />
                    <path d="M 300,52 C 300,85 440,85 440,118" fill="none" stroke="#cbd5e1" strokeWidth="1.5" markerEnd="url(#arrow)" />

                    {/* Parent node */}
                    <foreignObject x="175" y="10" width="250" height="44">
                      <div className="flex items-center justify-center h-full">
                        <div className="bg-blue-500 text-white text-xs font-semibold rounded-lg px-5 py-2.5 text-center shadow-sm whitespace-nowrap">
                          How do I get tenant insurance?
                        </div>
                      </div>
                    </foreignObject>

                    {/* Child node 1 */}
                    <foreignObject x="30" y="120" width="260" height="50">
                      <div className="flex items-center justify-center h-full">
                        <div className="bg-blue-500 text-white text-[11px] font-semibold rounded-lg px-4 py-2 text-center shadow-sm leading-tight">
                          Option 1: Coverage through your<br />parents' home insurance
                        </div>
                      </div>
                    </foreignObject>

                    {/* Child node 2 */}
                    <foreignObject x="310" y="120" width="260" height="50">
                      <div className="flex items-center justify-center h-full">
                        <div className="bg-blue-500 text-white text-[11px] font-semibold rounded-lg px-4 py-2 text-center shadow-sm leading-tight">
                          Option 2: Purchase a tenant<br />insurance policy
                        </div>
                      </div>
                    </foreignObject>
                  </svg>
                </div>

                {/* Option details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Option 1 */}
                  <div className="rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
                    <div className="bg-blue-50 border-b border-blue-100 px-4 py-2.5">
                      <p className="text-sm font-semibold text-blue-900">Option 1: Coverage Through Your Parents' Home Insurance</p>
                    </div>
                    <div className="mx-4 mt-3 flex items-start gap-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2.5">
                      <span className="text-sm shrink-0">💡</span>
                      <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                        <span className="font-semibold">Fun fact:</span> Over 90% of Amber Peak's student tenants are able to get coverage through their parents' home insurance policy.
                      </p>
                    </div>
                    <div className="px-4 py-3 space-y-2.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      <p>
                        If your parents have an active home insurance policy, it may extend coverage to you while you live away from home as a student. This typically applies only if you remain a dependent.
                      </p>
                      <div className="space-y-2">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">Step 1: Confirm Coverage</p>
                        <p>Contact your parents' insurance broker or provider to verify that you are covered while living away from home. Request that your coverage be formally extended if needed.</p>
                      </div>
                      <div className="space-y-2">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">Step 2: Obtain Written Confirmation</p>
                        <p>Ask for a confirmation letter or policy document that clearly states:</p>
                        <ul className="list-disc list-inside space-y-1 pl-1">
                          <li>That you are insured</li>
                          <li>The type and limits of your coverage</li>
                          <li>Any exclusions or conditions that apply</li>
                        </ul>
                      </div>
                      <p>Having this documentation ensures you understand your protection and can provide proof of coverage if required.</p>
                    </div>
                    <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2.5">
                      <AlertCircle size={14} className="text-gray-500 dark:text-gray-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                        <span className="font-semibold">Common mistake 🚨:</span> Do not request home insurance or real property insurance for your unit. You are looking for <span className="font-semibold">tenant insurance</span>, which covers your personal belongings and provides liability protection. Home insurance covers the building structure, which is your landlord's responsibility, not yours.
                      </p>
                    </div>
                  </div>

                  {/* Option 2 */}
                  <div className="rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
                    <div className="bg-blue-50 border-b border-blue-100 px-4 py-2.5">
                      <p className="text-sm font-semibold text-blue-900">Option 2: Tenant Insurance Policy</p>
                    </div>
                    <div className="px-4 py-3 space-y-2.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      <p>
                        You can purchase a tenant insurance policy specifically for your stay in this unit. These policies are straightforward to set up and are typically quite affordable.
                      </p>
                      <p>
                        A single policy can include one person or multiple people. If you would like to share coverage with the housemates you are living with, you can add them as named insureds on the same policy. Without doing so, coverage extends only to those who are specifically named on the policy.
                      </p>
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 pt-1">The following providers are ones that our tenants have had success with in the past:</p>
                      <div className="flex flex-col gap-2.5">
                        <a href="https://www.squareone.ca/tenant" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-3.5 hover:shadow-md hover:border-blue-300 hover:bg-blue-50/30 transition-all group">
                          <img src="/images/vendors/square_one_logo.png" alt="Square One Insurance" className="h-7 w-auto object-contain" />
                          <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors flex items-center gap-1">Visit site <span className="text-xs">↗</span></span>
                        </a>
                        <a href="https://www.sonnet.ca/tenant-insurance/ontario" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-3.5 hover:shadow-md hover:border-blue-300 hover:bg-blue-50/30 transition-all group">
                          <img src="/images/vendors/sonnet_logo.png" alt="Sonnet Insurance" className="h-6 w-auto object-contain" />
                          <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors flex items-center gap-1">Visit site <span className="text-xs">↗</span></span>
                        </a>
                        <a href="https://www.desjardinsgeneralinsurance.com/home-insurance/coverage/tenant" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-3.5 hover:shadow-md hover:border-blue-300 hover:bg-blue-50/30 transition-all group">
                          <img src="/images/vendors/desjardins_logo.png" alt="Desjardins Insurance" className="h-9 w-auto object-contain" />
                          <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors flex items-center gap-1">Visit site <span className="text-xs">↗</span></span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Email template for insurance broker */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Email Template for Your Insurance Broker</h4>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  {emailCopied ? (
                    <>
                      <CheckCircle2 size={14} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copy to clipboard
                    </>
                  )}
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  To help you get started, we've prepared a templated email you can use when reaching out to an insurance broker or provider. It includes all of the coverage requirements outlined in your lease agreement. Feel free to copy it and adjust it as needed before sending.
                </p>
                <div
                  className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-4 py-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line font-mono text-[12px] max-h-80 overflow-y-auto cursor-text select-all"
                  onClick={(e) => {
                    const range = document.createRange();
                    range.selectNodeContents(e.currentTarget);
                    const sel = window.getSelection();
                    sel?.removeAllRanges();
                    sel?.addRange(range);
                  }}
                >
                  {insuranceEmailText}
                </div>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    emailCopied
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {emailCopied ? 'Copied to clipboard' : 'Copy email template'}
                </button>
              </div>
            </div>

            {/* Insurance FAQ */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Insurance FAQs</h4>
              </div>
              <div className="px-5 py-2">
                <FaqAccordion items={INSURANCE_FAQ} />
              </div>
            </div>

            {/* Further reading */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
              <div className="px-5 py-5 space-y-3">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Further Reading</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Interested in learning more about tenant insurance from third-party sources? Here are some helpful articles.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <a href="https://www.tdinsurance.com/products-services/home-insurance/tips-advice/renters-insurance-101" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all">
                    TD Insurance: What is Tenant Insurance? <span className="text-xs opacity-60">↗</span>
                  </a>
                  <a href="https://www.torontorentals.com/blog/tenant-insurance-a-complete-guide-for-renters" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all">
                    TorontoRentals: Complete Guide for Renters <span className="text-xs opacity-60">↗</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          </CollapsibleSection>
        </section>

        {isByTheBed && (
        <section id="unit-roommates">
          <CollapsibleSection
            number={sectionStepById['unit-roommates'] ?? 3}
            title="Your Unit Roommates"
            isComplete={roommatesIntroduced}
            defaultOpen={!roommatesIntroduced}
            forceToggle={sectionToggle}
            sectionId="unit-roommates"
            forceOpenId={forceOpenId}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                You&apos;re renting a bedroom in a shared unit
                {unitRoommates?.unitNumber ? (
                  <> (Unit {unitRoommates.unitNumber})</>
                ) : null}
                . Below are the other tenants you&apos;ll be living with for your lease term—including you. Connect with them before move-in!
              </p>

              {unitRoommatesLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 size={18} className="animate-spin text-red-700 dark:text-red-400" />
                  Loading roommate information…
                </div>
              ) : unitRoommatesSorted.length > 0 ? (
                <ul className="space-y-3">
                  {unitRoommatesSorted.map((roommate) => {
                    const displayName = [roommate.firstName, roommate.lastName].filter(Boolean).join(' ') || 'Tenant';
                    const rowKey = `${roommate.bedLabel}-${roommate.isCurrentUser ? 'you' : displayName}`;
                    const phoneDisplay = roommate.phone
                      ? formatPhoneForTenantPortalDisplay(roommate.phone)
                      : null;
                    const phoneCopy = phoneCopyValue(roommate.phone);
                    const academicLine = [
                      roommate.yearOfStudy ? `Year ${roommate.yearOfStudy}` : null,
                      roommate.program,
                    ].filter(Boolean).join(' · ');

                    return (
                      <li
                        key={rowKey}
                        className={`rounded-xl border overflow-hidden ${
                          roommate.isCurrentUser
                            ? 'border-blue-200 bg-blue-50/40'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className={`px-4 py-3 border-b flex flex-wrap items-center justify-between gap-x-3 gap-y-2 ${
                          roommate.isCurrentUser
                            ? 'border-blue-100 bg-blue-50/80'
                            : 'border-gray-100 bg-gray-50'
                        }`}>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{displayName}</span>
                            {academicLine ? (
                              <>
                                <span className="text-gray-300 select-none" aria-hidden>·</span>
                                <span className="text-xs text-gray-600 dark:text-gray-400">{academicLine}</span>
                              </>
                            ) : null}
                            {roommate.isCurrentUser && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-800 bg-blue-100 border border-blue-200 rounded-full px-2 py-0.5">
                                You
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-full px-2 py-0.5 shrink-0">
                            {roommate.bedLabel}
                          </span>
                        </div>
                        <div className="px-4 py-3 space-y-2.5 text-sm">
                          <CopyContactRow
                            icon={Mail}
                            displayValue={roommate.email}
                            copyValue={roommate.email}
                            copyFieldId={`${rowKey}-email`}
                            copiedFieldId={copiedRoommateField}
                            onCopy={handleCopyRoommateField}
                            emptyLabel="Email not available yet"
                          />
                          <CopyContactRow
                            icon={Phone}
                            displayValue={phoneDisplay}
                            copyValue={phoneCopy}
                            copyFieldId={`${rowKey}-phone`}
                            copiedFieldId={copiedRoommateField}
                            onCopy={handleCopyRoommateField}
                            emptyLabel="Phone not available yet"
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-5 py-8 text-center">
                  <Users size={28} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No other roommates assigned yet</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                    As more bedrooms in your unit are leased, names and contact details will appear here automatically.
                  </p>
                </div>
              )}

              <div className={`rounded-xl p-5 space-y-3 border transition-colors ${
                roommatesIntroduced
                  ? 'bg-green-50 border-green-200'
                  : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    roommatesIntroduced ? 'bg-green-600' : 'bg-red-600'
                  }`}>
                    {roommatesIntroduced
                      ? <Check size={16} className="text-white" />
                      : <Users size={14} className="text-white" />
                    }
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${
                      roommatesIntroduced ? 'text-green-600' : 'text-red-500'
                    }`}>Optional</p>
                    <h4 className={`text-sm font-bold uppercase tracking-wide ${
                      roommatesIntroduced ? 'text-green-900' : 'text-gray-900'
                    }`}>Introduce yourself to your roommates</h4>
                  </div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  Reach out using the contact details above — a quick email or text to say hello goes a long way before move-in.
                  When you have introduced yourself, confirm below.
                </p>
                <label className={`flex items-center gap-3 cursor-pointer select-none ${
                  roommatesIntroduced ? 'text-green-800' : 'text-gray-800'
                }`}>
                  <input
                    type="checkbox"
                    checked={roommatesIntroduced}
                    onChange={(e) => {
                      void handleChecklistToggle(ROOMMATES_INTRODUCED_ITEM_KEY, e.target.checked);
                    }}
                    className="h-4 w-4 rounded border-gray-300 dark:border-white/15 text-red-700 dark:text-red-400 focus:ring-red-600"
                  />
                  <span className="text-sm font-medium">
                    I have introduced myself to my roommates
                  </span>
                </label>
              </div>
            </div>
          </CollapsibleSection>
        </section>
        )}

        {/* Section: Utility Account Confirmation (house manager only, exempt for certain units) */}
        {showUtilitiesSection && canManageMoveIn && (
        <section id="utilities">
          <CollapsibleSection number={sectionStepById['utilities'] ?? 3} title="Utility Account Confirmation" badge={showHouseManagerLabel ? 'House Manager' : undefined} isComplete={utilitiesSectionComplete} forceToggle={sectionToggle} sectionId="utilities" forceOpenId={forceOpenId}>

          <div className="space-y-4">
            {requiresCumEnrollmentForm ? (
              <>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
                  <p>
                    As per the terms of your lease, you are required to manage the utilities account for your unit starting on{' '}
                    <span className="font-medium text-gray-900 dark:text-white">{leaseStartLabel}</span>.
                  </p>
                  <p>
                    Utility billing at 390 Johnson Street is managed by Canadian Utility Management (CUM).
                    Your monthly invoice will include water, sewer, and electricity based on submetered consumption.
                  </p>
                </div>

                <div className="space-y-3 ml-1">
                  <CumUtilityNumberedStep number={1}>Download the PDF form below.</CumUtilityNumberedStep>
                  <CumUtilityNumberedStep number={2}>Complete and sign the PDF form.</CumUtilityNumberedStep>
                  <CumUtilityNumberedStep number={3}>
                    Email the signed copy to{' '}
                    <a href={`mailto:${CUM_BILLING_EMAIL}`} className="font-medium text-blue-600 hover:underline">
                      {CUM_BILLING_EMAIL}
                    </a>
                    .
                  </CumUtilityNumberedStep>
                  <CumUtilityNumberedStep number={4}>Upload the signed enrollment form to this portal.</CumUtilityNumberedStep>
                  <CumUtilityNumberedStep number={5}>
                    After CUM confirms your enrollment, upload their confirmation email to this portal.
                  </CumUtilityNumberedStep>
                </div>

                <CumUtilityEnrollmentFormPanel document={cumUtilityEnrollmentForm} />

                <UtilitiesDocumentUploadCard
                  stepNumber={4}
                  title="Upload Signed Utility Billing Enrollment Form"
                  description="Upload the completed and signed utility billing enrollment form (PDF or photo)."
                  hasDocument={utilitiesData?.hasEnrollmentFormDocument ?? false}
                  documentFilename={utilitiesData?.enrollmentFormDocumentFilename}
                  documentUrl={utilitiesData?.enrollmentFormDocumentUrl}
                  uploading={enrollmentUploading}
                  deleting={enrollmentDeleting}
                  dragOver={enrollmentDragOver}
                  onDragOverChange={setEnrollmentDragOver}
                  onUpload={handleEnrollmentFormUpload}
                  onDelete={handleEnrollmentFormDelete}
                  onBrowseClick={() => enrollmentFileRef.current?.click()}
                  uploadError={enrollmentUploadError}
                  dropzonePrompt={
                    <>Drag and drop your signed enrollment form here, or <span className="text-blue-600 underline underline-offset-2">browse files</span></>
                  }
                />
                <input
                  ref={enrollmentFileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleEnrollmentFormUpload(file);
                  }}
                />
              </>
            ) : (
              <>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
                  <p>
                    During lease signing, you were required to set up your Utilities Kingston account effective May 1st, 2026.
                  </p>
                  <p>
                    While you may have received confirmation that your request was submitted, this does not necessarily confirm the account has been fully activated.
                  </p>
                  <p>
                    We require confirmation that your account has been fully activated. Contact Utilities Kingston using the email template below to request written confirmation, then upload the confirmation document.
                  </p>
                  <p>
                    If your account has not been properly set up prior to move-in, utilities may be disconnected from the property or administrative fees may apply due to the complexity of changing the account after move-in.
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-white/5">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white">Email Template for Utilities Setup</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Copy each field below and paste into a new email to Utilities Kingston.</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    <div className="flex items-center gap-3 px-4 py-2.5">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-16 shrink-0">To</span>
                      <span className="flex-1 text-sm text-gray-800 dark:text-gray-100 font-mono">{utilitiesEmailTo}</span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(utilitiesEmailTo);
                          setUtilitiesToCopied(true);
                          setTimeout(() => setUtilitiesToCopied(false), 2000);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors shrink-0"
                      >
                        {utilitiesToCopied ? <><CheckCircle2 size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                      </button>
                    </div>

                    <div className="flex items-center gap-3 px-4 py-2.5">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-16 shrink-0">Subject</span>
                      <span className="flex-1 text-sm text-gray-800 dark:text-gray-100 font-mono truncate">{utilitiesEmailSubject}</span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(utilitiesEmailSubject);
                          setUtilitiesSubjectCopied(true);
                          setTimeout(() => setUtilitiesSubjectCopied(false), 2000);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors shrink-0"
                      >
                        {utilitiesSubjectCopied ? <><CheckCircle2 size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                      </button>
                    </div>

                    <div className="px-4 py-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Body</span>
                        <button
                          type="button"
                          onClick={handleCopyUtilitiesEmail}
                          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          {utilitiesEmailCopied ? <><CheckCircle2 size={12} /> Copied</> : <><Copy size={12} /> Copy body</>}
                        </button>
                      </div>
                      <div
                        className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-4 py-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line font-mono text-[12px] cursor-text select-all"
                        onClick={(e) => {
                          const range = document.createRange();
                          range.selectNodeContents(e.currentTarget);
                          const sel = window.getSelection();
                          sel?.removeAllRanges();
                          sel?.addRange(range);
                        }}
                      >
                        {utilitiesEmailText}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <UtilitiesDocumentUploadCard
              stepNumber={requiresCumEnrollmentForm ? 5 : undefined}
              title="Upload Utilities Confirmation"
              description={
                requiresCumEnrollmentForm
                  ? 'Upload the confirmation email you receive from Canadian Utility Management after they accept your enrollment form.'
                  : 'Upload your confirmation from Utilities Kingston showing that your account has been activated.'
              }
              hasDocument={utilitiesData?.hasUtilitiesDocument ?? false}
              documentFilename={utilitiesData?.documentFilename}
              documentUrl={utilitiesData?.documentUrl}
              showStaffReviewStatus
              staffReviewConfirmed={utilitiesData?.utilitiesAccountConfirmed ?? false}
              uploading={utilitiesUploading}
              deleting={utilitiesDeleting}
              dragOver={utilitiesDragOver}
              onDragOverChange={setUtilitiesDragOver}
              onUpload={handleUtilitiesUpload}
              onDelete={handleUtilitiesDelete}
              onBrowseClick={() => utilitiesFileRef.current?.click()}
              uploadError={utilitiesUploadError}
              dropzonePrompt={
                <>Drag and drop your confirmation here, or <span className="text-blue-600 underline underline-offset-2">browse files</span></>
              }
            />
            <input
              ref={utilitiesFileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUtilitiesUpload(file);
              }}
            />

            {/* Utilities confirmed checkbox */}
            {(() => {
              const hasDoc = utilitiesData?.hasUtilitiesDocument ?? false;
              const locked = !hasDoc;
              return (
                <div className={`rounded-xl border px-4 py-4 space-y-3 ${
                  utilitiesConfirmed ? 'bg-green-50 border-green-200' : locked ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <PackageCheck size={16} className={utilitiesConfirmed ? 'text-green-600' : locked ? 'text-gray-400' : 'text-red-500'} />
                    <div>
                      <p className={`text-[10px] uppercase tracking-widest font-semibold ${
                        utilitiesConfirmed ? 'text-green-600' : locked ? 'text-gray-400' : 'text-red-500'
                      }`}>Confirm</p>
                      <h4 className={`text-sm font-bold uppercase tracking-wide ${
                        utilitiesConfirmed ? 'text-green-900' : locked ? 'text-gray-500' : 'text-red-900'
                      }`}>
                        Utilities Account Confirmed
                      </h4>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    {utilitiesConfirmed
                      ? 'Utilities confirmation has been uploaded and confirmed.'
                      : locked
                        ? 'Upload your utilities confirmation document above before confirming.'
                        : 'Your utilities confirmation has been uploaded. Please confirm to proceed.'}
                  </p>

                  <div
                    role="checkbox"
                    aria-checked={utilitiesConfirmed}
                    aria-disabled={locked}
                    tabIndex={locked ? -1 : 0}
                    onClick={() => { if (!locked) handleToggleTask('utilities-confirmed', !utilitiesConfirmed); }}
                    onKeyDown={(e) => { if (!locked && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); handleToggleTask('utilities-confirmed', !utilitiesConfirmed); } }}
                    className={`flex items-center gap-3 ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer group'}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      utilitiesConfirmed
                        ? 'bg-green-600 border-green-600 text-white group-hover:bg-green-500 group-hover:border-green-500'
                        : locked
                          ? 'border-gray-300'
                          : 'border-red-400 group-hover:border-red-500'
                    }`}>
                      {utilitiesConfirmed && <Check size={13} strokeWidth={3} />}
                    </div>
                    <span className={`text-sm leading-snug ${utilitiesConfirmed ? 'text-green-800 line-through' : locked ? 'text-gray-400' : 'text-gray-800'}`}>
                      Utilities account has been confirmed
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>

          </CollapsibleSection>
        </section>
        )}

        {/* Section: Rent Split (house manager only) */}
        {showRentSplitSection && canManageMoveIn && (
        <section id="rent-split">
          <CollapsibleSection number={sectionStepById['rent-split'] ?? 4} title="Rent Split" badge={showHouseManagerLabel ? 'House Manager' : undefined} isComplete={rentSplitConfirmed} forceToggle={sectionToggle} sectionId="rent-split" forceOpenId={forceOpenId}>

          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Decide how the monthly rent will be split among all tenants on the lease.
              The amounts must add up to the total rent.
            </p>

            {rentSplitData?.totalRent && (
              <div className="flex items-center gap-2 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3">
                <DollarSign size={16} className="text-gray-500 dark:text-gray-400 shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Total monthly rent: <strong>${rentSplitData.totalRent.toFixed(2)}</strong>
                </span>
              </div>
            )}

            <div className="space-y-3">
              {rentSplitData?.tenants.map((t) => (
                <div key={t.tenantId} className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-2 min-w-0">
                    {t.isHouseManager && showHouseManagerLabel ? (
                      <Star size={14} className="text-amber-500 fill-amber-500 shrink-0" />
                    ) : (
                      <Users size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                    )}
                    <span className="text-sm text-gray-800 dark:text-gray-100 truncate">{t.firstName} {t.lastName}</span>
                    {t.isHouseManager && showHouseManagerLabel && (
                      <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-full px-1.5 py-0.5 shrink-0">
                        House Manager
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseFloat(rentSplitEdits[t.tenantId] || '0') || 0;
                        const next = Math.max(0, current - 10);
                        setRentSplitEdits((prev) => ({ ...prev, [t.tenantId]: next.toFixed(2) }));
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:text-gray-200 transition-colors shrink-0"
                    >
                      <Minus size={14} />
                    </button>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={rentSplitEdits[t.tenantId] ?? ''}
                        onChange={(e) => setRentSplitEdits((prev) => ({ ...prev, [t.tenantId]: e.target.value }))}
                        placeholder="0.00"
                        className="w-32 pl-7 pr-3 py-2 border border-gray-300 dark:border-white/15 rounded-lg text-sm text-right focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseFloat(rentSplitEdits[t.tenantId] || '0') || 0;
                        setRentSplitEdits((prev) => ({ ...prev, [t.tenantId]: (current + 10).toFixed(2) }));
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:text-gray-200 transition-colors shrink-0"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {rentSplitData?.totalRent != null && (
              <div className={`rounded-xl border px-4 py-3 space-y-1 ${
                rentSplitMatches
                  ? 'bg-green-50 border-green-200'
                  : rentSplitTotal > rentSplitData.totalRent
                    ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold ${
                    rentSplitMatches
                      ? 'text-green-700'
                      : rentSplitTotal > rentSplitData.totalRent
                        ? 'text-red-700'
                        : 'text-amber-700'
                  }`}>
                    Total: ${rentSplitTotal.toFixed(2)} / ${rentSplitData.totalRent.toFixed(2)}
                  </span>
                  {rentSplitMatches ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                      <CheckCircle2 size={14} />
                      Balanced
                    </span>
                  ) : (
                    <span className={`flex items-center gap-1 text-xs font-medium ${
                      rentSplitTotal > rentSplitData.totalRent ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      <AlertCircle size={14} />
                      {rentSplitTotal > rentSplitData.totalRent ? 'Over' : 'Under'} by ${Math.abs(rentSplitTotal - rentSplitData.totalRent).toFixed(2)}
                    </span>
                  )}
                </div>
                {!rentSplitMatches && (
                  <p className={`text-xs ${rentSplitTotal > rentSplitData.totalRent ? 'text-red-600' : 'text-amber-600'}`}>
                    The total must equal exactly ${rentSplitData.totalRent.toFixed(2)} before you can save.
                  </p>
                )}
              </div>
            )}

            <button
              onClick={handleSaveRentSplit}
              disabled={!rentSplitMatches || rentSplitSaving}
              className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                rentSplitMatches
                  ? 'bg-red-700 text-white hover:bg-red-800'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {rentSplitSaving ? 'Saving...' : rentSplitMatches ? 'Confirm Rent Split' : 'Amounts must equal total rent'}
            </button>

            {/* Rent split confirmed checkbox */}
            <div className={`rounded-xl border px-4 py-4 space-y-3 ${
              rentSplitConfirmed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center gap-2">
                <PackageCheck size={16} className={rentSplitConfirmed ? 'text-green-600' : 'text-gray-400'} />
                <div>
                  <p className={`text-[10px] uppercase tracking-widest font-semibold ${
                    rentSplitConfirmed ? 'text-green-600' : 'text-gray-400'
                  }`}>Confirm</p>
                  <h4 className={`text-sm font-bold uppercase tracking-wide ${
                    rentSplitConfirmed ? 'text-green-900' : 'text-gray-500'
                  }`}>Rent Split Confirmed</h4>
                </div>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                {rentSplitConfirmed
                  ? 'The rent split has been confirmed and saved. Uncheck below if you need to make changes.'
                  : 'Enter the correct amounts above and click "Confirm Rent Split" to complete this task.'}
              </p>

              <div
                role="checkbox"
                aria-checked={rentSplitConfirmed}
                aria-disabled={!rentSplitConfirmed}
                tabIndex={rentSplitConfirmed ? 0 : -1}
                onClick={() => { if (rentSplitConfirmed) handleToggleTask('rent-split-confirmed', false); }}
                onKeyDown={(e) => { if (rentSplitConfirmed && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); handleToggleTask('rent-split-confirmed', false); } }}
                className={`flex items-center gap-3 ${rentSplitConfirmed ? 'cursor-pointer group' : 'opacity-50 cursor-not-allowed'}`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                  rentSplitConfirmed
                    ? 'bg-green-600 border-green-600 text-white group-hover:bg-green-500 group-hover:border-green-500'
                    : 'border-gray-300'
                }`}>
                  {rentSplitConfirmed && <Check size={13} strokeWidth={3} />}
                </div>
                <span className={`text-sm leading-snug ${rentSplitConfirmed ? 'text-green-800 line-through' : 'text-gray-400'}`}>
                  Rent split has been confirmed
                </span>
              </div>
            </div>
          </div>

          </CollapsibleSection>
        </section>
        )}

      </div>
    </>
  );
}
